import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
 Button as MuiButton,
 Chip,
 IconButton,
 Stack,
 TextField,
 Tooltip,
} from "@mui/material";
import { FaWhatsapp } from "react-icons/fa";
import { MdElectricScooter } from "react-icons/md";
import {
 FiActivity,
 FiArrowDown,
 FiArrowUp,
 FiCheckCircle,
 FiEdit3,
 FiEye,
 FiFileText,
 FiGrid,
 FiHome,
 FiImage,
 FiLogIn,
 FiMail,
 FiMinus,
 FiMoon,
 FiPackage,
 FiPhone,
 FiPlus,
 FiRefreshCw,
 FiSave,
 FiSearch,
 FiSun,
 FiTrash2,
 FiUsers,
 FiX,
 FiXCircle,
} from "react-icons/fi";
import "./App.css";
import {
 AdminDialog,
 GridShell,
 IconAction,
 Notice,
 Stat,
} from "./components/common/AdminUI";
import Sidebar from "./components/layout/Sidebar";
import ScootyFeatureEditor from "./components/ScootyFeatureEditor/ScootyFeatureEditor";
import {
 clearStatus as clearAdminStatus,
 deleteEnquiry as deleteEnquiryAction,
 loadAdminData as loadAdminDataAction,
 loadProductsPage as loadProductsPageAction,
 updateCategoryOrder as updateCategoryOrderAction,
 updateProductOrder as updateProductOrderAction,
} from "./features/admin/adminSlice";
import {
 logout as logoutAdmin,
 setCredentials,
} from "./features/admin/authSlice";

const localApi = "http://localhost:5050/api";
const productionApi = "https://api.mozogroup.in/api";
const browserHost =
 typeof window !== "undefined" ? window.location.hostname : "";
const API_BASE = (
 process.env.REACT_APP_API_URL ||
 (browserHost === "localhost" || browserHost === "127.0.0.1"
  ? localApi
  : productionApi)
).replace(/\/$/, "");
const COMPANY_LOGO_URL = "https://mozogroup.in/logo.jpeg";
const ID_PREFIX = "jantarides.ev";

const pages = [
 { id: "dashboard", label: "Dashboard", icon: FiHome },
 { id: "sliders", label: "Slider", icon: FiImage },
 { id: "aboutSliders", label: "About Slider", icon: FiImage },
 { id: "journeyImages", label: "Journey Images", icon: FiImage },
 { id: "communityImages", label: "Community Images", icon: FiImage },
 { id: "customerFeedback", label: "Feedback", icon: FiUsers },
 //  { id: "scootyFeatures", label: "Scooty Features", icon: MdElectricScooter },
 { id: "systemCertifications", label: "System Certs", icon: FiFileText },
 { id: "productCertifications", label: "Product Certs", icon: FiFileText },
 { id: "categories", label: "Categories", icon: FiGrid },
 { id: "products", label: "Products", icon: FiPackage },
 { id: "stock", label: "Stock", icon: FiActivity },
 { id: "enquiries", label: "Enquiries", icon: FiUsers },
];

const emptyLogin = {
 email: "admin@mozo.local",
 password: "admin123",
};

const emptyCategory = {
 id: "",
 name: "",
 description: "",
 icon: "battery",
 displayOrder: "0",
};

const emptyProduct = {
 id: "",
 category: "",
 name: "",
 spec: "",
 price: "",
 rating: "4.5",
 discountType: "none",
 discountValue: "0",
 gstPercent: "0",
 minQty: "1",
 unit: "pcs",
 optionGroupName: "Size",
 stock: "0",
};

const productDetailSections = [
 {
  key: "specifications",
  label: "Specifications",
  placeholder: "Voltage 48V / Capacity 30Ah",
 },
 {
  key: "applications",
  label: "Applications",
  placeholder: "EV battery packs",
 },
 { key: "features", label: "Features", placeholder: "High quality build" },
 { key: "benefits", label: "Benefits", placeholder: "Reliable procurement" },
 {
  key: "sizes",
  label: "Available Sizes",
  placeholder: "48x30 / 4MM / 10 pcs pack",
 },
];

const emptyVariantRow = {
 rowKey: "",
 label: "",
 unit: "pcs",
 minQty: "1",
 stock: "0",
 price: "",
};

const createVariantRowKey = () =>
 `variant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createVariantRow = (overrides = {}) => ({
 ...emptyVariantRow,
 rowKey: createVariantRowKey(),
 ...overrides,
});

const productUnitOptions = [
 "pcs",
 "nos",
 "set",
 "pair",
 "meter",
 "inch",
 "cm",
 "mm",
 "liter",
 "ml",
 "kg",
 "gram",
 "roll",
 "sheet",
 "box",
 "pack",
 "bundle",
];

const emptySlider = {
 id: "",
 title: "",
 image: "",
 sortOrder: "0",
 active: "true",
};

const emptyCertification = {
 id: "",
 name: "",
 logo: "",
 certificateImage: "",
 sortOrder: "0",
 active: "true",
};
const emptyFeedback = {
 id: "",
 name: "",
 company: "",
 quote: "",
 image: "",
 sortOrder: "0",
 active: "true",
};

function decodeHtmlEntities(value = "") {
 const text = String(value ?? "");
 if (!text) return "";
 if (typeof document !== "undefined") {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
 }
 return text
  .replace(/&nbsp;|&#160;|&#xa0;/gi, " ")
  .replace(/&amp;/gi, "&")
  .replace(/&lt;/gi, "<")
  .replace(/&gt;/gi, ">")
  .replace(/&quot;/gi, '"')
  .replace(/&#039;|&#39;|&apos;/gi, "'")
  .replace(/&#x([0-9a-f]+);/gi, (_match, hex) =>
   String.fromCodePoint(parseInt(hex, 16)),
  )
  .replace(/&#(\d+);/g, (_match, num) =>
   String.fromCodePoint(parseInt(num, 10)),
  );
}

function displayText(value, fallback = "") {
 let text = decodeHtmlEntities(value);
 text = decodeHtmlEntities(text);
 const clean = text
  .replace(/<[^>]*>/g, " ")
  .replace(/\s+/g, " ")
  .trim();
 return clean || fallback;
}
function plainInputText(value = "") {
 return displayText(value, "");
}

function currency(value) {
 return new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
 }).format(Number(value || 0));
}

function slugify(value) {
 return value
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");
}

function buildEntityId(type, name) {
 const slug = slugify(name || "");

 return slug ? `${ID_PREFIX}.${type}.${slug}` : `${ID_PREFIX}.${type}.auto`;
}

function isFormDataBody(body) {
 return typeof FormData !== "undefined" && body instanceof FormData;
}

function createFormData(values, fileField = "image") {
 const formData = new FormData();
 const fileList = values[fileField];

 Object.entries(values).forEach(([key, value]) => {
  if (key === fileField || value === undefined || value === null) {
   return;
  }

  formData.append(key, value);
 });

 if (fileList?.length) {
  Array.from(fileList).forEach((file) => {
   formData.append(fileField, file);
  });
 }

 return formData;
}

function getProductImages(product) {
 const images = Array.isArray(product?.images) ? product.images : [];
 return [...new Set([...images, product?.image].filter(Boolean))];
}

function normalizeDetailRows(items) {
 const rows = Array.isArray(items)
  ? items.map((item) => plainInputText(item)).filter(Boolean)
  : [];
 return rows.length ? rows : [""];
}

function detailRowsToJson(rows) {
 return JSON.stringify(
  (Array.isArray(rows) ? rows : [])
   .map((item) => String(item || "").trim())
   .filter(Boolean),
 );
}

function escapeHtml(value = "") {
 return String(value)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");
}

function cleanDetailLine(label, value) {
 const clean = displayText(value);
 if (!clean || clean.toLowerCase() === "n/a") return null;
 return `${label}: ${clean}`;
}

function buildAdminWhatsappUrl(enquiry) {
 const number = String(enquiry?.whatsapp?.number || "918700338552").replace(
  /\D/g,
  "",
 );
 if (!number) return "";

 const customer = enquiry.customer || {};
 const quote = enquiry.quote || {};
 const items = Array.isArray(quote.items) ? quote.items : [];
 const customerLines = [
  cleanDetailLine("Name", customer.name),
  cleanDetailLine("Company", customer.companyName),
  cleanDetailLine("Phone", customer.phone),
  cleanDetailLine("Email", customer.email),
  cleanDetailLine("GST", customer.gstNumber),
  cleanDetailLine("Address", customer.address),
 ].filter(Boolean);
 const productLines = items.length
  ? items.map((item, index) =>
     [
      `${index + 1}. ${displayText(item.name, "Product")}`,
      item.productId ? `   ID: ${item.productId}` : null,
      `   Qty: ${Number(item.quantity || 0)} ${item.unit || "pcs"}`,
      `   Rate: ${currency(item.unitPrice)}`,
      `   Amount: ${currency(item.lineTotal)}`,
     ]
      .filter(Boolean)
      .join("\n"),
    )
  : ["No products selected"];
 const message = [
  "*MOZO EV - QUOTATION REQUEST*",
  `Enquiry ID: ${enquiry.enquiryId}`,
  `Date: ${new Date(enquiry.createdAt || Date.now()).toLocaleString("en-IN")}`,
  "",
  "*Customer Details*",
  ...(customerLines.length ? customerLines : ["No customer details added"]),
  "",
  "*Quoted Products*",
  ...productLines,
  "",
  "*Summary*",
  `Total Quantity: ${Number(quote.totalQuantity || 0)}`,
  `Subtotal: ${currency(quote.subtotal)}`,
  `GST: ${currency(quote.gst)}`,
  `Grand Total: ${currency(quote.total)}`,
  "",
  "Final rate and availability will be confirmed by Mozo sales team.",
 ].join("\n");

 return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}
function buildAdminQuotationHtml(enquiry) {
 const customer = enquiry.customer || {};
 const quote = enquiry.quote || {};
 const items = quote.items || [];
 const rows = items.length
  ? items
     .map(
      (item, index) => `
       <tr>
        <td>${index + 1}</td>
        <td><strong>${escapeHtml(displayText(item.name))}</strong><small>${escapeHtml(item.productId || "")}</small></td>
        <td>${Number(item.quantity || 0)}</td>
        <td>${currency(item.unitPrice)}</td>
        <td>${currency(item.lineTotal)}</td>
       </tr>`,
     )
     .join("")
  : `<tr><td colspan="5">No products selected</td></tr>`;

 return `<!doctype html><html><head><meta charset="utf-8" /><title>${escapeHtml(enquiry.enquiryId)} Quotation</title>
 <style>*{box-sizing:border-box}body{margin:0;background:#eef4f8;color:#0f172a;font-family:Arial,sans-serif}.sheet{width:min(960px,calc(100% - 32px));margin:24px auto;background:#fff;border:1px solid #d8e3ec;border-radius:18px;overflow:hidden;box-shadow:0 24px 70px rgba(15,23,42,.14)}.hero{background:linear-gradient(135deg,#073042,#0f766e);color:#fff;padding:34px 38px;display:flex;justify-content:space-between;gap:24px}.logo-row{display:flex;align-items:center;gap:14px}.logo-row img{width:62px;height:46px;object-fit:contain;background:#fff;border-radius:10px;padding:6px}.badge{display:inline-block;margin-bottom:10px;padding:7px 12px;border-radius:999px;background:rgba(249,115,22,.18);color:#fed7aa;font-weight:700;font-size:12px;text-transform:uppercase}.brand h1{margin:0 0 8px;font-size:34px}.brand p,.meta p{margin:4px 0;color:#d9f99d}.section{padding:28px 38px}h2{margin:0 0 14px;font-size:18px}.details{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.details div{border:1px solid #e2e8f0;background:#f8fafc;border-radius:12px;padding:12px 14px}.details span{display:block;color:#64748b;font-size:12px;text-transform:uppercase;font-weight:700;margin-bottom:4px}.message{white-space:pre-wrap;border-left:4px solid #f97316;background:#fff7ed;padding:14px 16px;border-radius:12px;color:#334155}table{width:100%;border-collapse:collapse}th{background:#0f172a;color:#fff;text-align:left;padding:13px 12px;font-size:13px;text-transform:uppercase}td{border-bottom:1px solid #e2e8f0;padding:14px 12px;vertical-align:top}td small{display:block;color:#64748b;margin-top:4px}td:nth-child(3),td:nth-child(4),td:nth-child(5),th:nth-child(3),th:nth-child(4),th:nth-child(5){text-align:right}.totals{margin-left:auto;width:min(360px,100%);display:grid;gap:8px}.totals div{display:flex;justify-content:space-between;border-bottom:1px solid #e2e8f0;padding:9px 0}.totals .grand{margin-top:4px;padding:14px 16px;border:0;border-radius:12px;background:#16a34a;color:#fff;font-size:20px;font-weight:800}.footer{padding:20px 38px 30px;color:#64748b;font-size:13px}.print{position:fixed;right:20px;bottom:20px;border:0;border-radius:999px;padding:12px 18px;background:#f97316;color:#fff;font-weight:800;cursor:pointer}@media print{body{background:#fff}.sheet{width:100%;margin:0;border:0;border-radius:0;box-shadow:none}.print{display:none}}@media(max-width:720px){.hero,.details{display:grid;grid-template-columns:1fr}.hero,.section,.footer{padding:24px}}</style></head><body><main class="sheet"><header class="hero"><div class="brand"><div class="logo-row"><img src="${escapeHtml(COMPANY_LOGO_URL)}" alt="Mozo EV" /><div><span class="badge">Quotation Request</span><h1>Mozo EV</h1><p>Battery, BMS, connector and EV component supply</p></div></div></div><div class="meta"><p><strong>Enquiry:</strong> ${escapeHtml(enquiry.enquiryId)}</p><p><strong>Date:</strong> ${escapeHtml(new Date(enquiry.createdAt || Date.now()).toLocaleString("en-IN"))}</p><p><strong>Status:</strong> ${escapeHtml(enquiry.status || "new")}</p></div></header><section class="section"><h2>Customer Details</h2><div class="details">${[
  ["Name", customer.name],
  ["Company", customer.companyName],
  ["Phone", customer.phone],
  ["Email", customer.email],
  ["GST Number", customer.gstNumber],
  ["Address", customer.address],
 ]
  .filter(([, value]) => value)
  .map(
   ([label, value]) =>
    `<div><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`,
  )
  .join(
   "",
  )}</div></section><section class="section"><h2>Quoted Products</h2><table><thead><tr><th>#</th><th>Product</th><th>Qty</th><th>Unit</th><th>Total</th></tr></thead><tbody>${rows}</tbody></table></section><section class="section"><div class="totals"><div><span>Total Quantity</span><strong>${Number(quote.totalQuantity || 0)}</strong></div><div><span>Subtotal</span><strong>${currency(quote.subtotal)}</strong></div><div><span>GST</span><strong>${currency(quote.gst)}</strong></div><div class="grand"><span>Grand Total</span><strong>${currency(quote.total)}</strong></div></div></section><footer class="footer">Final pricing and availability will be confirmed by Mozo sales team.</footer></main><button class="print" onclick="window.print()">Print / Save PDF</button></body></html>`;
}

function LoginPage({ loginForm, setLoginForm, login, loading }) {
 return (
  <main className="login-page">
   <section className="login-card">
    <div className="login-brand">
     <span>mz</span>
     <div>
      <strong>Mozo Admin</strong>
      <small>Secure dashboard access</small>
     </div>
    </div>

    <div>
     <h1>Sign in</h1>
     <p>
      Dashboard, products, categories, enquiries and quotes are locked until
      admin login.
     </p>
    </div>

    <form className="login-form" onSubmit={login}>
     <label>
      Email
      <input
       value={loginForm.email}
       onChange={(event) =>
        setLoginForm({ ...loginForm, email: event.target.value })
       }
       placeholder="admin@mozo.local"
       type="email"
       required
      />
     </label>
     <label>
      Password
      <input
       value={loginForm.password}
       onChange={(event) =>
        setLoginForm({ ...loginForm, password: event.target.value })
       }
       placeholder="admin123"
       type="password"
       required
      />
     </label>
     <button type="submit" disabled={loading}>
      <FiLogIn /> Sign in
     </button>
    </form>
   </section>
  </main>
 );
}

function DashboardPage({
 categories,
 products,
 enquiries,
 totals,
 setActivePage,
}) {
 return (
  <section className="stats">
   <Stat
    icon={FiGrid}
    label="Categories"
    value={categories.length}
    tone="#635bff"
    change="+12%"
    positive
    onClick={() => setActivePage("categories")}
   />

   <Stat
    icon={FiPackage}
    label="Products"
    value={products.length}
    tone="#16a34a"
    change="+4%"
    positive
    onClick={() => setActivePage("products")}
   />

   <Stat
    icon={FiUsers}
    label="Open Enquiries"
    value={totals.openEnquiries}
    tone="#f97316"
    change="0%"
    onClick={() => setActivePage("enquiries")}
   />

   <Stat
    icon={FiActivity}
    label="Stock Value"
    value={currency(totals.value)}
    tone="#eab308"
    change="-5%"
    negative
    onClick={() => setActivePage("stock")}
   />
  </section>
  // <section className="page-stack">
  //  <section className="stats">
  //   <Stat
  //    icon={FiGrid}
  //    label="Categories"
  //    value={categories.length}
  //    tone="#111111"
  //   />
  //   <Stat
  //    icon={FiPackage}
  //    label="Products"
  //    value={products.length}
  //    tone="#3f3f46"
  //   />
  //   <Stat
  //    icon={FiUsers}
  //    label="Open Enquiries"
  //    value={totals.openEnquiries}
  //    tone="#525252"
  //   />
  //   <Stat
  //    icon={FiActivity}
  //    label="Stock Value"
  //    value={currency(totals.value)}
  //    tone="#737373"
  //   />
  //  </section>

  //  <section className="quick-grid">
  //   <button type="button" onClick={() => setActivePage("categories")}>
  //    <FiGrid /> Add category
  //   </button>
  //   <button type="button" onClick={() => setActivePage("products")}>
  //    <FiPackage /> Add product
  //   </button>
  //   <button type="button" onClick={() => setActivePage("enquiries")}>
  //    <FiUsers /> View enquiries
  //   </button>
  //  </section>
  // </section>
 );
}

function CategoriesPage({
 categories,
 editingCategory,
 setEditingCategory,
 saveCategory,
 editCategory,
 deleteCategory,
 updateCategorySequence,
}) {
 const [formOpen, setFormOpen] = useState(false);
 const [query, setQuery] = useState("");
 const {
  register,
  handleSubmit,
  reset,
  watch,
  formState: { isSubmitting },
 } = useForm({
  defaultValues: emptyCategory,
 });
 const categoryName = watch("name");
 const selectedImage = watch("image")?.[0];
 const categoryIdPreview =
  editingCategory || buildEntityId("category", categoryName);
 const currentCategory = categories.find(
  (category) => category.id === editingCategory,
 );
 const categoryImagePreview = selectedImage
  ? URL.createObjectURL(selectedImage)
  : currentCategory?.image;

 useEffect(() => {
  if (!selectedImage || !categoryImagePreview?.startsWith("blob:"))
   return undefined;
  return () => URL.revokeObjectURL(categoryImagePreview);
 }, [categoryImagePreview, selectedImage]);

 const filteredCategories = categories.filter((category) => {
  const term = query.trim().toLowerCase();
  return term
   ? `${category.name} ${category.id} ${category.icon}`
      .toLowerCase()
      .includes(term)
   : true;
 });

 function openAddForm() {
  setEditingCategory("");
  reset(emptyCategory);
  setFormOpen(true);
 }

 function openEditForm(category) {
  editCategory(category);
  reset({
   id: category.id,
   name: category.name,
   description: category.description || "",
   icon: category.icon,
   displayOrder: String(category.displayOrder ?? 0),
  });
  setFormOpen(true);
 }

 async function submitCategory(values) {
  const saved = await saveCategory(values);
  if (saved) {
   reset(emptyCategory);
   setFormOpen(false);
  }
 }

 const categoryColumns = [
  {
   field: "name",
   headerName: "Name",
   flex: 1.1,
   minWidth: 190,
   renderCell: ({ row }) => (
    <strong>{displayText(row.name, "Untitled product")}</strong>
   ),
  },
  {
   field: "displayOrder",
   headerName: "Order",
   width: 94,
   type: "number",
   renderCell: ({ row }) => <strong>{Number(row.displayOrder ?? 0)}</strong>,
  },
  {
   field: "image",
   headerName: "Image",
   width: 82,
   sortable: false,
   renderCell: ({ row }) => (
    <span className="grid-thumb">
     {row.image ? (
      <img src={row.image} alt={displayText(row.name, "Product image")} />
     ) : (
      <FiImage />
     )}
    </span>
   ),
  },
  { field: "id", headerName: "ID", flex: 1.1, minWidth: 220 },
  {
   field: "description",
   headerName: "Description",
   flex: 1.4,
   minWidth: 260,
   valueGetter: (value) => value || "No description",
  },
  {
   field: "actions",
   headerName: "Actions",
   width: 214,
   minWidth: 214,
   sortable: false,
   align: "center",
   headerAlign: "center",
   renderCell: ({ row }) => (
    <Stack className="grid-action-stack" direction="row" spacing={0.5}>
     <IconAction
      icon={FiArrowUp}
      label="Move category up"
      onClick={() => updateCategorySequence(row.id, -1)}
     />
     <IconAction
      icon={FiArrowDown}
      label="Move category down"
      onClick={() => updateCategorySequence(row.id, 1)}
     />
     <IconAction
      icon={FiEdit3}
      label="Edit category"
      onClick={() => openEditForm(row)}
     />
     <IconAction
      icon={FiTrash2}
      label="Delete category"
      onClick={() => deleteCategory(row.id)}
      tone="danger"
     />
    </Stack>
   ),
  },
 ];

 return (
  <section className="page-stack">
   <div className="page-toolbar">
    <div>
     <p>Category Management</p>
     <h1>Categories</h1>
    </div>
    <button type="button" onClick={openAddForm}>
     <FiPlus /> Add Category
    </button>
   </div>

   <AdminDialog
    eyebrow={editingCategory ? "Edit category" : "New category"}
    onClose={() => setFormOpen(false)}
    open={formOpen}
    title={editingCategory ? "Update category" : "Add category"}
   >
    <form
     className="form-grid split-form"
     onSubmit={handleSubmit(submitCategory)}
    >
     <label>
      Category ID
      <input value={categoryIdPreview} readOnly />
     </label>
     <label>
      Name
      <input
       {...register("name", { required: true })}
       placeholder="Lithium Cells"
      />
     </label>
     <label>
      Icon
      <input {...register("icon", { required: true })} placeholder="battery" />
     </label>
     <label>
      Display Order
      <input
       {...register("displayOrder")}
       min="0"
       placeholder="0"
       type="number"
      />
     </label>
     <label className="wide">
      Description
      <textarea
       {...register("description")}
       placeholder="Short category detail shown on user website"
      />
     </label>
     <label className="wide">
      Image
      <input
       {...register("image")}
       accept="image/jpeg,image/png,image/webp,image/gif"
       type="file"
      />
     </label>
     <div className="image-upload-preview wide">
      {categoryImagePreview ? (
       <img
        src={categoryImagePreview}
        alt={categoryName || currentCategory?.name || "Category preview"}
       />
      ) : (
       <span>
        <FiImage /> No image selected
       </span>
      )}
      <small>
       {selectedImage?.name ||
        currentCategory?.image ||
        "Upload JPEG, PNG, WebP, or GIF up to 5 MB."}
      </small>
     </div>
     <div className="actions">
      <button type="submit" disabled={isSubmitting}>
       <FiSave /> Save Category
      </button>
      <button
       className="ghost"
       type="button"
       onClick={() => reset(emptyCategory)}
      >
       Clear
      </button>
     </div>
    </form>
   </AdminDialog>
   <article className="panel table-panel">
    <div className="panel-head">
     <div>
      <p>Category Management</p>
      <h2>Categories</h2>
      <small>{filteredCategories.length} categories</small>
     </div>
     <div className="filters">
      <label>
       <FiSearch />
       <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Filter by name or id"
       />
      </label>
      <button type="button" onClick={openAddForm}>
       <FiPlus /> Add Category
      </button>
     </div>
    </div>
    <GridShell
     rows={filteredCategories}
     columns={categoryColumns}
     getRowId={(row) => row.id}
     emptyText="No categories match this filter."
    />
   </article>
  </section>
 );
}

function ProductDetailFields({
 detailRows,
 addDetailRow,
 updateDetailRow,
 removeDetailRow,
}) {
 return (
  <div className="product-detail-builder wide">
   <div className="variant-builder-head">
    <div>
     <strong>Product Detail Sections</strong>
     <small>
      Add only the rows needed for this product. Empty rows are ignored.
     </small>
    </div>
   </div>
   {productDetailSections.map((section) => (
    <div className="detail-section-editor" key={section.key}>
     <div className="detail-section-head">
      <strong>{section.label}</strong>
      <button type="button" onClick={() => addDetailRow(section.key)}>
       <FiPlus /> Add
      </button>
     </div>
     {(detailRows[section.key] || [""]).map((value, index) => (
      <div className="detail-row-editor" key={`${section.key}-${index}`}>
       <input
        className="detail-row-input"
        value={value}
        onChange={(event) =>
         updateDetailRow(section.key, index, event.target.value)
        }
        placeholder={section.placeholder}
       />
       <button
        className="variant-remove"
        type="button"
        onClick={() => removeDetailRow(section.key, index)}
        aria-label={`Remove ${section.label} row`}
       >
        <FiTrash2 />
       </button>
      </div>
     ))}
    </div>
   ))}
  </div>
 );
}

function ProductDetailsDialog({ product, onClose }) {
 if (!product) return null;
 const images = getProductImages(product);
 const sectionItems = (key) =>
  Array.isArray(product[key])
   ? product[key].map((item) => String(item || "").trim()).filter(Boolean)
   : [];

 return (
  <AdminDialog
   eyebrow="Product view"
   maxWidth="lg"
   onClose={onClose}
   open={Boolean(product)}
   title={displayText(product.name, "Product details")}
  >
   <div className="product-view-modal">
    <div className="product-view-summary">
     <span className="grid-thumb large">
      {product.image ? (
       <img
        src={product.image}
        alt={displayText(product.name, "Product image")}
       />
      ) : (
       <FiImage />
      )}
     </span>
     <div>
      <h3>{displayText(product.name, "Untitled product")}</h3>
      <p>{displayText(product.spec, "No description added.")}</p>
      <div className="view-chip-row">
       <Chip
        label={product.category || "No category"}
        size="small"
        variant="outlined"
       />
       <Chip
        label={`GST ${Number(product.gstPercent || 0)}%`}
        size="small"
        variant="outlined"
       />
       <Chip
        label={`Rating ${Number(product.rating || 4.5).toFixed(1)}`}
        size="small"
        variant="outlined"
       />
       <Chip
        label={`Stock ${product.stock || 0} ${product.unit || "pcs"}`}
        size="small"
        variant="outlined"
       />
      </div>
     </div>
    </div>
    <div className="product-view-grid">
     <InfoBlock title="Price" value={currency(product.price)} />
     <InfoBlock
      title="Final Price"
      value={currency(
       product.priceWithGst || product.finalPrice || product.price,
      )}
     />
     <InfoBlock
      title="MOQ"
      value={`${product.minQty || 1} ${product.unit || "pcs"}`}
     />
     <InfoBlock
      title="Option Group"
      value={product.optionGroupName || "Size"}
     />
    </div>
    {product.variants?.length ? (
     <ProductViewSection
      title="Variants"
      items={product.variants.map(
       (variant) =>
        `${variant.label} - ${variant.stock || 0} ${variant.unit || product.unit || "pcs"} - ${variant.price ? currency(variant.price) : "Default price"}`,
      )}
     />
    ) : null}
    {images.length ? (
     <ProductViewSection title="Images" items={images} imageItems />
    ) : null}
    {productDetailSections.map((section) => (
     <ProductViewSection
      key={section.key}
      title={section.label}
      items={sectionItems(section.key)}
     />
    ))}
   </div>
  </AdminDialog>
 );
}

function InfoBlock({ title, value }) {
 return (
  <div className="info-block">
   <span>{title}</span>
   <strong>{value}</strong>
  </div>
 );
}

function ProductViewSection({ title, items, imageItems = false }) {
 if (!items?.length) return null;
 return (
  <section className="product-view-section">
   <h4>{title}</h4>
   {imageItems ? (
    <div className="product-view-images">
     {items.map((item) => (
      <img key={item} src={item} alt="" />
     ))}
    </div>
   ) : (
    <ul>
     {items.map((item) => (
      <li key={item}>{item}</li>
     ))}
    </ul>
   )}
  </section>
 );
}

function ProductsPage({
 categories,
 products,
 filteredProducts,
 editingProduct,
 setEditingProduct,
 saveProduct,
 editProduct,
 deleteProduct,
 updateProductSequence,
 productPagination,
 loadProductsPage,
 query,
 setQuery,
 selectedCategory,
 setSelectedCategory,
 priceFilter,
 setPriceFilter,
}) {
 const [formOpen, setFormOpen] = useState(false);
 const [selectedProductImages, setSelectedProductImages] = useState([]);
 const [retainedProductImages, setRetainedProductImages] = useState([]);
 const [variantRows, setVariantRows] = useState([emptyVariantRow]);
 const [productFormError, setProductFormError] = useState("");
 const [detailRows, setDetailRows] = useState(() =>
  productDetailSections.reduce(
   (acc, section) => ({ ...acc, [section.key]: [""] }),
   {},
  ),
 );
 const [viewingProduct, setViewingProduct] = useState(null);
 const {
  register,
  handleSubmit,
  reset,
  watch,
  setValue,
  formState: { errors, isSubmitting },
 } = useForm({
  defaultValues: emptyProduct,
 });
 const stock = Number(watch("stock") || 0);
 const selectedImages = useMemo(
  () => selectedProductImages.slice(0, 10),
  [selectedProductImages],
 );
 const currentProduct = products.find(
  (product) => product.id === editingProduct,
 );
 const existingProductImages = retainedProductImages;
 const selectedImagePreviews = useMemo(
  () => selectedImages.map((image) => URL.createObjectURL(image)),
  [selectedImages],
 );

 useEffect(() => {
  if (selectedImages.length === 0) return undefined;
  return () => {
   selectedImagePreviews.forEach((preview) => {
    if (preview?.startsWith("blob:")) {
     URL.revokeObjectURL(preview);
    }
   });
  };
 }, [selectedImagePreviews, selectedImages.length]);

 useEffect(() => {
  if (!formOpen || editingProduct) return;
  setValue("category", categories[0]?.id || "");
 }, [categories, editingProduct, formOpen, setValue]);

 function openAddForm() {
  setProductFormError("");
  setEditingProduct("");
  setSelectedProductImages([]);
  setRetainedProductImages([]);
  setVariantRows([createVariantRow({ unit: categories[0]?.unit || "pcs" })]);
  setDetailRows(
   productDetailSections.reduce(
    (acc, section) => ({ ...acc, [section.key]: [""] }),
    {},
   ),
  );
  reset({ ...emptyProduct, category: categories[0]?.id || "" });
  setFormOpen(true);
 }

 function openEditForm(product) {
  setProductFormError("");
  editProduct(product);
  setSelectedProductImages([]);
  setRetainedProductImages(getProductImages(product));
  setVariantRows(
   product.variants?.length
    ? product.variants.map((variant) =>
       createVariantRow({
        label: variant.label || "",
        unit: variant.unit || product.unit || "pcs",
        minQty: String(variant.minQty || product.minQty || 1),
        stock: String(variant.stock ?? product.stock ?? 0),
        price: variant.price ? String(variant.price) : "",
       }),
      )
    : [
       createVariantRow({
        unit: product.unit || "pcs",
        minQty: String(product.minQty || 1),
        stock: String(product.stock || 0),
        price: "",
       }),
      ],
  );
  reset({
   id: product.id,
   category: product.category,
   name: displayText(product.name),
   spec: product.spec,
   price: String(product.price),
   rating: String(product.rating || 4.5),
   discountType: product.discountType || "none",
   discountValue: String(product.discountValue || 0),
   gstPercent: String(product.gstPercent || 0),
   minQty: String(product.minQty),
   unit: product.unit || "pcs",
   optionGroupName: product.optionGroupName || "Size",
   stock: String(product.stock),
  });
  setDetailRows(
   productDetailSections.reduce(
    (acc, section) => ({
     ...acc,
     [section.key]: normalizeDetailRows(product[section.key]),
    }),
    {},
   ),
  );
  setFormOpen(true);
 }

 function addVariantRow() {
  setVariantRows((current) => [
   ...current,
   createVariantRow({
    unit: watch("unit") || "pcs",
    minQty: String(watch("minQty") || 1),
    stock: String(watch("stock") || 0),
   }),
  ]);
 }

 function updateVariantRow(index, key, value) {
  setVariantRows((current) =>
   current.map((row, rowIndex) =>
    rowIndex === index ? { ...row, [key]: value } : row,
   ),
  );
 }

 function removeVariantRow(index) {
  setVariantRows((current) =>
   current.length <= 1
    ? [createVariantRow({ unit: watch("unit") || "pcs" })]
    : current.filter((_, rowIndex) => rowIndex !== index),
  );
 }

 function addDetailRow(sectionKey) {
  setDetailRows((current) => ({
   ...current,
   [sectionKey]: [...(current[sectionKey] || [""]), ""],
  }));
 }

 function updateDetailRow(sectionKey, index, value) {
  setDetailRows((current) => ({
   ...current,
   [sectionKey]: (current[sectionKey] || [""]).map((item, rowIndex) =>
    rowIndex === index ? value : item,
   ),
  }));
 }

 function removeDetailRow(sectionKey, index) {
  setDetailRows((current) => {
   const nextRows = (current[sectionKey] || [""]).filter(
    (_, rowIndex) => rowIndex !== index,
   );
   return {
    ...current,
    [sectionKey]: nextRows.length ? nextRows : [""],
   };
  });
 }

 function addProductImages(event) {
  const nextFiles = Array.from(event.target.files || []);
  setSelectedProductImages((current) =>
   [...current, ...nextFiles].slice(0, 10),
  );
  event.target.value = "";
 }

 function removeSelectedImage(indexToRemove) {
  setSelectedProductImages((current) =>
   current.filter((_, index) => index !== indexToRemove),
  );
 }

 function removeRetainedImage(imageToRemove) {
  setRetainedProductImages((current) =>
   current.filter((image) => image !== imageToRemove),
  );
 }

 async function submitProduct(values) {
  setProductFormError("");
  const saved = await saveProduct({
   ...values,
   name: plainInputText(values.name),
   spec: plainInputText(values.spec),
   variants: JSON.stringify(
    variantRows
     .map((variant) => ({
      label: String(variant.label || "").trim(),
      unit: variant.unit || values.unit || "pcs",
      minQty: Number(variant.minQty || values.minQty || 1),
      stock: Number(variant.stock ?? values.stock ?? 0),
      price: variant.price === "" ? 0 : Number(variant.price || 0),
     }))
     .filter((variant) => variant.label),
   ),
   specifications: detailRowsToJson(detailRows.specifications),
   applications: detailRowsToJson(detailRows.applications),
   features: detailRowsToJson(detailRows.features),
   benefits: detailRowsToJson(detailRows.benefits),
   sizes: detailRowsToJson(detailRows.sizes),
   images: selectedImages,
   retainedImages: JSON.stringify(retainedProductImages),
  });
  if (saved) {
   setSelectedProductImages([]);
   setRetainedProductImages([]);
   setVariantRows([createVariantRow({ unit: values.unit || "pcs" })]);
   setDetailRows(
    productDetailSections.reduce(
     (acc, section) => ({ ...acc, [section.key]: [""] }),
     {},
    ),
   );
   reset({ ...emptyProduct, category: categories[0]?.id || "" });
   setFormOpen(false);
  } else {
   setProductFormError(
    "Product save nahi hua. Neeche/error toast me message check karein, phir dobara try karein.",
   );
  }
 }

 function handleProductInvalid(formErrors) {
  const fieldLabels = {
   category: "Category",
   price: "Price",
   name: "Name",
   spec: "Spec",
   minQty: "Min Qty",
   unit: "Unit",
   stock: "Stock",
  };
  const firstField = Object.keys(formErrors)[0];
  const firstLabel = fieldLabels[firstField] || "Required field";
  setProductFormError(
   `${firstLabel} required hai. Product save karne se pehle is field ko fill karein.`,
  );
 }

 const productColumns = [
  {
   field: "name",
   headerName: "Product",
   flex: 1.35,
   minWidth: 220,
   renderCell: ({ row }) => (
    <div className="grid-main-cell">
     <strong>{displayText(row.name, "Untitled product")}</strong>
    </div>
   ),
  },
  // {
  //  field: "displayOrder",
  //  headerName: "Order",
  //  width: 94,
  //  type: "number",
  //  renderCell: ({ row }) => <strong>{Number(row.displayOrder ?? 0)}</strong>,
  // },
  {
   field: "image",
   headerName: "Image",
   width: 82,
   sortable: false,
   renderCell: ({ row }) => (
    <span className="grid-thumb">
     {row.image ? (
      <img src={row.image} alt={displayText(row.name, "Product image")} />
     ) : (
      <FiImage />
     )}
    </span>
   ),
  },
  { field: "category", headerName: "Category", width: 135 },
  {
   field: "price",
   headerName: "Price",
   width: 132,
   renderCell: ({ row }) => (
    <div className="grid-price-cell">
     <strong>{currency(row.hasDiscount ? row.finalPrice : row.price)}</strong>
     {row.hasDiscount ? (
      <Chip
       className="discount-chip"
       label={`${currency(row.price)} off: ${row.discountLabel}`}
       size="small"
      />
     ) : null}
    </div>
   ),
  },
  {
   field: "rating",
   headerName: "Rating",
   width: 78,
   valueGetter: (value) => Number(value || 4.5).toFixed(1),
  },
  {
   field: "gstPercent",
   headerName: "GST",
   width: 68,
   valueGetter: (value) => `${Number(value || 0)}%`,
  },
  {
   field: "unit",
   headerName: "Unit",
   width: 70,
   valueGetter: (value, row) => value || row.unit || "pcs",
  },
  {
   field: "stock",
   headerName: "Stock",
   width: 86,
   renderCell: ({ row }) => (
    <Chip
     color={Number(row.stock || 0) <= 0 ? "error" : "success"}
     label={row.stock || 0}
     size="small"
     variant="outlined"
    />
   ),
  },
  {
   field: "actions",
   headerName: "Actions",
   width: 222,
   minWidth: 222,
   sortable: false,
   align: "center",
   headerAlign: "center",
   renderCell: ({ row }) => (
    <Stack className="grid-action-stack" direction="row" spacing={0.5}>
     <IconAction
      icon={FiArrowUp}
      label="Move product up"
      onClick={() => updateProductSequence(row.id, -1)}
     />
     <IconAction
      icon={FiArrowDown}
      label="Move product down"
      onClick={() => updateProductSequence(row.id, 1)}
     />
     <IconAction
      icon={FiEye}
      label="View product details"
      onClick={() => setViewingProduct(row)}
     />
     <IconAction
      icon={FiEdit3}
      label="Edit product"
      onClick={() => openEditForm(row)}
     />
     <IconAction
      icon={FiTrash2}
      label="Delete product"
      onClick={() => deleteProduct(row.id)}
      tone="danger"
     />
    </Stack>
   ),
  },
 ];

 const productPreviewName = watch("name") || currentProduct?.name || "Product";

 return (
  <section className="page-stack">
   <div className="page-toolbar">
    <div>
     <p>Product Management</p>
     <h1>Products</h1>
    </div>
    <button type="button" onClick={openAddForm}>
     <FiPlus /> Add Product
    </button>
   </div>

   <AdminDialog
    eyebrow={editingProduct ? "Edit product" : "New product"}
    maxWidth="lg"
    onClose={() => setFormOpen(false)}
    open={formOpen}
    title={editingProduct ? "Update product" : "Add product"}
   >
    <form
     className="product-form"
     onSubmit={handleSubmit(submitProduct, handleProductInvalid)}
     noValidate
    >
     {productFormError ? (
      <div className="form-alert form-alert-error wide" role="alert">
       {productFormError}
      </div>
     ) : null}
     <label className="product-top-field">
      Category
      <select
       {...register("category", { required: "Category required hai." })}
       className={errors.category ? "input-error" : ""}
      >
       <option value="">Select category</option>
       {categories.map((category) => (
        <option key={category.id} value={category.id}>
         {displayText(category.name)}
        </option>
       ))}
      </select>
      {errors.category ? (
       <small className="field-error">{errors.category.message}</small>
      ) : null}
     </label>
     <label className="product-top-field">
      Price
      <input
       {...register("price", {
        required: "Price required hai.",
        min: { value: 0, message: "Price 0 se kam nahi ho sakta." },
       })}
       className={errors.price ? "input-error" : ""}
       min="0"
       type="number"
      />
      {errors.price ? (
       <small className="field-error">{errors.price.message}</small>
      ) : null}
     </label>
     <label className="product-top-field">
      Rating
      <input
       {...register("rating", { min: 0, max: 5 })}
       max="5"
       min="0"
       step="0.1"
       type="number"
      />
     </label>
     <label className="product-top-field">
      Discount Type
      <select {...register("discountType")}>
       <option value="none">No discount</option>
       <option value="percent">Percent off</option>
       <option value="amount">Amount off</option>
      </select>
     </label>
     <label className="product-top-field">
      Discount Value
      <input
       {...register("discountValue", { min: 0 })}
       min="0"
       step="0.01"
       type="number"
      />
     </label>
     <label className="product-top-field">
      GST %
      <input
       {...register("gstPercent", { min: 0, max: 100 })}
       max="100"
       min="0"
       step="0.01"
       type="number"
      />
     </label>
     <label className="product-meta-field wide">
      Name
      <input
       {...register("name", { required: "Name required hai." })}
       className={errors.name ? "input-error" : ""}
       placeholder="High Drain Cell 3200 mAh"
      />
      {errors.name ? (
       <small className="field-error">{errors.name.message}</small>
      ) : null}
     </label>
     <label className="product-meta-field wide">
      Specifications
      <textarea
       {...register("spec", { required: "Specifications required hai." })}
       className={errors.spec ? "input-error" : ""}
       placeholder="3.7V lithium ion cell for battery packs"
       rows="3"
      />
      {errors.spec ? (
       <small className="field-error">{errors.spec.message}</small>
      ) : null}
     </label>
     <label className="product-meta-field">
      Min Qty
      <input
       {...register("minQty", {
        required: "Min Qty required hai.",
        min: { value: 1, message: "Min Qty kam se kam 1 honi chahiye." },
       })}
       className={errors.minQty ? "input-error" : ""}
       min="1"
       type="number"
      />
      {errors.minQty ? (
       <small className="field-error">{errors.minQty.message}</small>
      ) : null}
     </label>
     <label className="product-meta-field">
      Unit
      <select
       {...register("unit", { required: "Unit required hai." })}
       className={errors.unit ? "input-error" : ""}
      >
       {productUnitOptions.map((unit) => (
        <option key={unit} value={unit}>
         {unit}
        </option>
       ))}
      </select>
      {errors.unit ? (
       <small className="field-error">{errors.unit.message}</small>
      ) : null}
     </label>
     <label className="product-meta-field">
      Option Name
      <input
       {...register("optionGroupName")}
       placeholder="Size, Diameter, Pack, Colour"
      />
     </label>
     <div className="variant-builder wide">
      <div className="variant-builder-head">
       <div>
        <strong>Product Variants</strong>
        <small>
         Use separate rows for diameter, pack size, color, model or capacity.
        </small>
       </div>
       <button type="button" onClick={addVariantRow}>
        <FiPlus /> Add Variant
       </button>
      </div>
      <div className="variant-row variant-row-header">
       <span>Label</span>
       <span>Unit</span>
       <span>Min Qty</span>
       <span>Stock</span>
       <span>Price</span>
       <span />
      </div>
      {variantRows.map((variant, index) => (
       <div className="variant-row" key={variant.rowKey || index}>
        <input
         value={variant.label}
         onChange={(event) =>
          updateVariantRow(index, "label", event.target.value)
         }
         placeholder={index === 0 ? "0.75MM / 10 pcs pack" : "Variant label"}
        />
        <select
         value={variant.unit}
         onChange={(event) =>
          updateVariantRow(index, "unit", event.target.value)
         }
        >
         {productUnitOptions.map((unit) => (
          <option key={unit} value={unit}>
           {unit}
          </option>
         ))}
        </select>
        <input
         min="1"
         type="number"
         value={variant.minQty}
         onChange={(event) =>
          updateVariantRow(index, "minQty", event.target.value)
         }
        />
        <input
         min="0"
         type="number"
         value={variant.stock}
         onChange={(event) =>
          updateVariantRow(index, "stock", event.target.value)
         }
        />
        <input
         min="0"
         step="0.01"
         type="number"
         value={variant.price}
         onChange={(event) =>
          updateVariantRow(index, "price", event.target.value)
         }
         placeholder="Optional"
        />
        <button
         className="variant-remove"
         type="button"
         onClick={() => removeVariantRow(index)}
         aria-label="Remove variant"
        >
         <FiTrash2 />
        </button>
       </div>
      ))}
     </div>
     <ProductDetailFields
      addDetailRow={addDetailRow}
      detailRows={detailRows}
      removeDetailRow={removeDetailRow}
      updateDetailRow={updateDetailRow}
     />
     <label className="product-meta-field">
      Stock
      <div className="stepper-field">
       <button
        type="button"
        onClick={() => setValue("stock", String(Math.max(0, stock - 1)))}
        aria-label="Decrease stock"
       >
        <FiMinus />
       </button>
       <input
        {...register("stock", {
         required: "Stock required hai.",
         min: { value: 0, message: "Stock 0 se kam nahi ho sakta." },
        })}
        className={errors.stock ? "input-error" : ""}
        min="0"
        type="number"
       />
       <button
        type="button"
        onClick={() => setValue("stock", String(stock + 1))}
        aria-label="Increase stock"
       >
        <FiPlus />
       </button>
      </div>
      {errors.stock ? (
       <small className="field-error">{errors.stock.message}</small>
      ) : null}
     </label>
     <label className="wide">
      Images
      <input
       accept="image/jpeg,image/png,image/webp,image/gif"
       multiple
       onChange={addProductImages}
       type="file"
      />
     </label>
     <div className="image-upload-preview image-upload-preview-grid wide">
      {existingProductImages.length || selectedImagePreviews.length ? (
       <div className="preview-strip">
        {existingProductImages.map((preview, index) => (
         <span className="preview-thumb" key={`existing-${preview}`}>
          <img
           src={preview}
           alt={`${productPreviewName} saved preview ${index + 1}`}
          />
          <button
           type="button"
           onClick={() => removeRetainedImage(preview)}
           aria-label="Remove saved image"
          >
           <FiX />
          </button>
         </span>
        ))}
        {selectedImagePreviews.map((preview, index) => (
         <span className="preview-thumb" key={`${preview}-${index}`}>
          <img
           src={preview}
           alt={`${productPreviewName} new preview ${index + 1}`}
          />
          <button
           type="button"
           onClick={() => removeSelectedImage(index)}
           aria-label="Remove selected image"
          >
           <FiX />
          </button>
         </span>
        ))}
       </div>
      ) : (
       <span>
        <FiImage /> No images selected
       </span>
      )}
      <small>
       {selectedImages.length
        ? `${selectedImages.length} new image${selectedImages.length === 1 ? "" : "s"} selected. Maximum 10 images.`
        : existingProductImages.length
          ? `${existingProductImages.length} saved image${existingProductImages.length === 1 ? "" : "s"}. Choose new files to replace them.`
          : "Upload up to 10 JPEG, PNG, WebP, or GIF images. Large images are compressed by backend."}
      </small>
     </div>
     <div className="actions wide">
      <button type="submit" disabled={isSubmitting}>
       <FiSave /> Save Product
      </button>
      <button
       className="ghost"
       type="button"
       onClick={() => {
        setSelectedProductImages([]);
        setRetainedProductImages([]);
        setVariantRows([createVariantRow({ unit: watch("unit") || "pcs" })]);
        setDetailRows(
         productDetailSections.reduce(
          (acc, section) => ({ ...acc, [section.key]: [""] }),
          {},
         ),
        );
        reset({ ...emptyProduct, category: categories[0]?.id || "" });
       }}
      >
       Clear
      </button>
     </div>
    </form>
   </AdminDialog>

   <article className="panel table-panel">
    <div className="panel-head table-head">
     <div>
      <p>Product Management</p>
      <h2>Products</h2>
      <small>
       {filteredProducts.length} of {products.length} products
      </small>
     </div>
     <div className="filters product-filters">
      <label>
       <FiSearch />
       <input
        value={query}
        onChange={(event) => {
         const value = event.target.value;
         setQuery(value);
         loadProductsPage({
          page: 1,
          limit: Number(productPagination?.limit || 100),
          category: selectedCategory,
          q: value,
         });
        }}
        placeholder="Filter by name"
       />
      </label>
      <input
       value={priceFilter}
       onChange={(event) => {
        const value = event.target.value;
        setPriceFilter(value);
        loadProductsPage({
         page: 1,
         limit: Number(productPagination?.limit || 100),
         maxPrice: value,
        });
       }}
       min="0"
       placeholder="Max price"
       type="number"
      />
      <select
       value={selectedCategory}
       onChange={(event) => {
        const value = event.target.value;
        setSelectedCategory(value);
        loadProductsPage({
         page: 1,
         limit: Number(productPagination?.limit || 100),
         category: value,
         q: query,
        });
       }}
      >
       <option value="">All categories</option>
       {categories.map((category) => (
        <option key={category.id} value={category.id}>
         {displayText(category.name)}
        </option>
       ))}
      </select>
      <button type="button" onClick={openAddForm}>
       <FiPlus /> Add Product
      </button>
     </div>
    </div>
    <GridShell
     rows={filteredProducts}
     columns={productColumns}
     getRowId={(row) => row.id}
     emptyText="No products match this filter."
     rowCount={productPagination?.total || filteredProducts.length}
     paginationMode="server"
     paginationModel={{
      page: Math.max(0, Number(productPagination?.page || 1) - 1),
      pageSize: Number(productPagination?.limit || 100),
     }}
     onPaginationModelChange={(model) =>
      loadProductsPage({
       page: Number(model.page || 0) + 1,
       limit: Number(model.pageSize || 100),
       category: selectedCategory,
       q: query,
      })
     }
    />
    <div className="table legacy-hidden" aria-hidden="true">
     <div className="table-header slider-table">
      <span>Name</span>
      <span>Image</span>
      <span>Category</span>
      <span>Price</span>
      <span>Rating</span>
      <span>Stock</span>
      <span>Actions</span>
     </div>
     {filteredProducts.map((product) => (
      <div className="table-row product-table" key={product.id}>
       <div>
        <strong>{displayText(product.name, "Untitled product")}</strong>
        <small>
         {product.id} - {displayText(product.spec)}
        </small>
       </div>
       <span className="thumb-cell">
        {product.image ? (
         <img
          src={product.image}
          alt={displayText(product.name, "Product image")}
         />
        ) : (
         <FiImage />
        )}
       </span>
       <span>{product.category}</span>
       <span>
        {product.hasDiscount ? (
         <>
          <strong>{currency(product.finalPrice)}</strong>
          <small>
           {currency(product.price)} Ãƒâ€šÃ‚Â· {product.discountLabel}
          </small>
         </>
        ) : (
         currency(product.price)
        )}
       </span>
       <span>{Number(product.rating || 4.5).toFixed(1)}</span>
       <span>{product.stock}</span>
       <div className="row-actions">
        <button
         type="button"
         onClick={() => openEditForm(product)}
         aria-label="Edit product"
        >
         <FiEdit3 />
        </button>
        <button
         type="button"
         onClick={() => deleteProduct(product.id)}
         aria-label="Delete product"
        >
         <FiTrash2 />
        </button>
       </div>
      </div>
     ))}
    </div>
   </article>
   <ProductDetailsDialog
    product={viewingProduct}
    onClose={() => setViewingProduct(null)}
   />
  </section>
 );
}

function StockPage({ products, updateProductStock }) {
 const [query, setQuery] = useState("");
 const [drafts, setDrafts] = useState({});

 useEffect(() => {
  setDrafts(
   products.reduce(
    (nextDrafts, product) => ({
     ...nextDrafts,
     [product.id]: String(product.stock || 0),
    }),
    {},
   ),
  );
 }, [products]);

 const filteredStockProducts = products.filter((product) => {
  const term = query.trim().toLowerCase();
  return term
   ? `${displayText(product.name)} ${product.id} ${displayText(product.category)}`
      .toLowerCase()
      .includes(term)
   : true;
 });

 function changeDraft(productId, value) {
  setDrafts((current) => ({
   ...current,
   [productId]: String(Math.max(0, Number(value || 0))),
  }));
 }

 const stockColumns = [
  {
   field: "name",
   headerName: "Product",
   flex: 1.4,
   minWidth: 260,
   renderCell: ({ row }) => (
    <div className="grid-main-cell">
     <strong>{displayText(row.name, "Untitled product")}</strong>
     <small>{row.id}</small>
    </div>
   ),
  },
  { field: "category", headerName: "Category", flex: 0.9, minWidth: 160 },
  {
   field: "stock",
   headerName: "Current",
   width: 110,
   renderCell: ({ row }) => (
    <Chip
     color={Number(row.stock || 0) <= 0 ? "error" : "success"}
     label={row.stock || 0}
     size="small"
     variant="outlined"
    />
   ),
  },
  {
   field: "updateStock",
   headerName: "Update Stock",
   width: 210,
   sortable: false,
   renderCell: ({ row }) => {
    const draftValue = drafts[row.id] ?? String(row.stock || 0);
    const draftNumber = Number(draftValue || 0);

    return (
     <div className="grid-stepper">
      <IconButton
       aria-label="Decrease stock"
       onClick={() => changeDraft(row.id, draftNumber - 1)}
       size="small"
      >
       <FiMinus />
      </IconButton>
      <TextField
       size="small"
       type="number"
       value={draftValue}
       onChange={(event) => changeDraft(row.id, event.target.value)}
       inputProps={{ min: 0 }}
      />
      <IconButton
       aria-label="Increase stock"
       onClick={() => changeDraft(row.id, draftNumber + 1)}
       size="small"
      >
       <FiPlus />
      </IconButton>
     </div>
    );
   },
  },
  {
   field: "actions",
   headerName: "Action",
   width: 150,
   sortable: false,
   renderCell: ({ row }) => (
    <MuiButton
     onClick={() =>
      updateProductStock(row.id, Number(drafts[row.id] ?? row.stock ?? 0))
     }
     size="small"
     startIcon={<FiSave />}
     variant="contained"
    >
     Save
    </MuiButton>
   ),
  },
 ];

 return (
  <section className="page-stack">
   <div className="page-toolbar">
    <div>
     <p>Inventory Control</p>
     <h1>Stock Management</h1>
    </div>
   </div>

   <article className="panel table-panel">
    <div className="panel-head table-head">
     <div>
      <p>Inventory Control</p>
      <h2>Stock Management</h2>
      <small>{filteredStockProducts.length} products</small>
     </div>
     <div className="filters">
      <label>
       <FiSearch />
       <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search product or category"
       />
      </label>
     </div>
    </div>

    <GridShell
     rows={filteredStockProducts}
     columns={stockColumns}
     getRowId={(row) => row.id}
     emptyText="No stock records match this filter."
    />
    <div className="table legacy-hidden" aria-hidden="true">
     <div className="table-header stock-table">
      <span>Product</span>
      <span>Category</span>
      <span>Current</span>
      <span>Update Stock</span>
      <span>Action</span>
     </div>
     {filteredStockProducts.map((product) => {
      const draftValue = drafts[product.id] ?? String(product.stock || 0);
      const draftNumber = Number(draftValue || 0);

      return (
       <div className="table-row stock-table" key={product.id}>
        <div>
         <strong>{displayText(product.name, "Untitled product")}</strong>
         <small>{product.id}</small>
        </div>
        <span>{product.category}</span>
        <span className={Number(product.stock || 0) <= 0 ? "danger-text" : ""}>
         {product.stock || 0}
        </span>
        <div className="stepper-field">
         <button
          type="button"
          onClick={() => changeDraft(product.id, draftNumber - 1)}
          aria-label="Decrease stock"
         >
          <FiMinus />
         </button>
         <input
          min="0"
          type="number"
          value={draftValue}
          onChange={(event) => changeDraft(product.id, event.target.value)}
         />
         <button
          type="button"
          onClick={() => changeDraft(product.id, draftNumber + 1)}
          aria-label="Increase stock"
         >
          <FiPlus />
         </button>
        </div>
        <button
         type="button"
         onClick={() => updateProductStock(product.id, draftNumber)}
        >
         <FiSave /> Save Stock
        </button>
       </div>
      );
     })}
    </div>
   </article>
  </section>
 );
}

function SlidersPage({
 sliders,
 editingSlider,
 setEditingSlider,
 saveSlider,
 editSlider,
 deleteSlider,
 eyebrow = "Homepage Slider",
 pageTitle = "Slider",
 titlePlaceholder = "Homepage banner 1",
 recommendedSize = "1920 x 650 px",
 emptyText = "No slider images saved.",
}) {
 const [formOpen, setFormOpen] = useState(false);
 const {
  register,
  handleSubmit,
  reset,
  watch,
  formState: { isSubmitting },
 } = useForm({
  defaultValues: emptySlider,
 });
 const title = watch("title");
 const selectedImage = watch("imageFile")?.[0];
 const currentSlider = sliders.find((slider) => slider.id === editingSlider);
 const preview = selectedImage
  ? URL.createObjectURL(selectedImage)
  : currentSlider?.image || watch("image");

 useEffect(() => {
  if (!selectedImage || !preview?.startsWith("blob:")) return undefined;
  return () => URL.revokeObjectURL(preview);
 }, [preview, selectedImage]);

 function openAddForm() {
  setEditingSlider("");
  reset(emptySlider);
  setFormOpen(true);
 }

 function openEditForm(slider) {
  editSlider(slider);
  reset({
   id: slider.id,
   title: slider.title,
   image: slider.image,
   sortOrder: String(slider.sortOrder || 0),
   active: String(slider.active !== false),
  });
  setFormOpen(true);
 }

 async function submitSlider(values) {
  const saved = await saveSlider(values);
  if (saved) {
   reset(emptySlider);
   setFormOpen(false);
  }
 }

 const sliderColumns = [
  {
   field: "title",
   headerName: "Reference",
   flex: 1.3,
   minWidth: 240,
   renderCell: ({ row }) => (
    <div className="grid-main-cell">
     <strong>{row.title || row.id}</strong>
     <small>{row.id}</small>
    </div>
   ),
  },
  {
   field: "displayOrder",
   headerName: "Order",
   width: 94,
   type: "number",
   renderCell: ({ row }) => <strong>{Number(row.displayOrder ?? 0)}</strong>,
  },
  {
   field: "image",
   headerName: "Image",
   width: 110,
   sortable: false,
   renderCell: ({ row }) => (
    <span className="grid-thumb banner-thumb">
     {row.image ? <img src={row.image} alt={row.title} /> : <FiImage />}
    </span>
   ),
  },
  { field: "sortOrder", headerName: "Order", width: 100 },
  {
   field: "active",
   headerName: "Status",
   width: 130,
   renderCell: ({ row }) => (
    <Chip
     color={row.active === false ? "default" : "success"}
     label={row.active === false ? "Hidden" : "Active"}
     size="small"
     variant="outlined"
    />
   ),
  },
  {
   field: "actions",
   headerName: "Actions",
   width: 130,
   sortable: false,
   align: "center",
   headerAlign: "center",
   renderCell: ({ row }) => (
    <Stack className="grid-action-stack" direction="row" spacing={0.5}>
     <IconAction
      icon={FiEdit3}
      label="Edit slide"
      onClick={() => openEditForm(row)}
     />
     <IconAction
      icon={FiTrash2}
      label="Delete slide"
      onClick={() => deleteSlider(row.id)}
      tone="danger"
     />
    </Stack>
   ),
  },
 ];

 return (
  <section className="page-stack">
   <div className="page-toolbar">
    <div>
     <p>{eyebrow}</p>
     <h1>{pageTitle}</h1>
    </div>
    <button type="button" onClick={openAddForm}>
     <FiPlus /> Add Slide
    </button>
   </div>

   <AdminDialog
    eyebrow={editingSlider ? "Edit slide" : "New slide"}
    onClose={() => setFormOpen(false)}
    open={formOpen}
    title={editingSlider ? "Update slider image" : "Add slider image"}
   >
    <form className="product-form" onSubmit={handleSubmit(submitSlider)}>
     <label>
      Sort Order
      <input {...register("sortOrder")} min="0" type="number" />
     </label>
     <label>
      Reference Title
      <input {...register("title")} placeholder={titlePlaceholder} />
     </label>
     <label>
      Active
      <select {...register("active")}>
       <option value="true">Active</option>
       <option value="false">Hidden</option>
      </select>
     </label>
     <label className="wide">
      Image URL
      <input {...register("image")} placeholder="https://..." />
     </label>
     <label className="wide">
      Upload Image
      <input
       {...register("imageFile")}
       accept="image/jpeg,image/png,image/webp,image/gif"
       type="file"
      />
     </label>
     <div className="image-upload-preview wide">
      {preview ? (
       <img src={preview} alt={title || "Slider preview"} />
      ) : (
       <span>
        <FiImage /> No image selected
       </span>
      )}
      <small>
       Recommended banner size: {recommendedSize}. Put all text/design inside
       the image.
      </small>
     </div>
     <div className="actions wide">
      <button type="submit" disabled={isSubmitting}>
       <FiSave /> Save Slide
      </button>
      <button
       className="ghost"
       type="button"
       onClick={() => reset(emptySlider)}
      >
       Clear
      </button>
     </div>
    </form>
   </AdminDialog>

   <article className="panel table-panel">
    <div className="panel-head">
     <div>
      <p>{eyebrow}</p>
      <h2>{pageTitle}</h2>
      <small>{sliders.length} slides</small>
     </div>
     <div className="filters">
      <button type="button" onClick={openAddForm}>
       <FiPlus /> Add Slide
      </button>
     </div>
    </div>
    <GridShell
     rows={sliders}
     columns={sliderColumns}
     getRowId={(row) => row.id}
     emptyText={emptyText}
    />
    <div className="table legacy-hidden" aria-hidden="true">
     <div className="table-header product-table">
      <span>Reference</span>
      <span>Image</span>
      <span>Size</span>
      <span>Status</span>
      <span>Actions</span>
     </div>
     {sliders.map((slider) => (
      <div className="table-row slider-table" key={slider.id}>
       <div>
        <strong>{slider.title || slider.id}</strong>
        <small>{slider.id}</small>
       </div>
       <span className="thumb-cell">
        {slider.image ? (
         <img src={slider.image} alt={slider.title} />
        ) : (
         <FiImage />
        )}
       </span>
       <span>{recommendedSize}</span>
       <span className="chip">
        {slider.active === false ? "Hidden" : "Active"}
       </span>
       <div className="row-actions">
        <button
         type="button"
         onClick={() => openEditForm(slider)}
         aria-label="Edit slider"
        >
         <FiEdit3 />
        </button>
        <button
         type="button"
         onClick={() => deleteSlider(slider.id)}
         aria-label="Delete slider"
        >
         <FiTrash2 />
        </button>
       </div>
      </div>
     ))}
    </div>
   </article>
  </section>
 );
}

function CertificationsPage({
 certifications,
 editingCertification,
 setEditingCertification,
 saveCertification,
 editCertification,
 deleteCertification,
 eyebrow = "About Page",
 pageTitle = "Certifications",
 emptyText = "No certifications saved.",
}) {
 const [formOpen, setFormOpen] = useState(false);
 const {
  register,
  handleSubmit,
  reset,
  watch,
  formState: { isSubmitting },
 } = useForm({ defaultValues: emptyCertification });
 const selectedLogo = watch("logoFile")?.[0];
 const selectedCertificate = watch("certificateFile")?.[0];
 const currentCertification = certifications.find(
  (item) => item.id === editingCertification,
 );
 const logoPreview = selectedLogo
  ? URL.createObjectURL(selectedLogo)
  : currentCertification?.logo || watch("logo");
 const certificatePreview = selectedCertificate
  ? URL.createObjectURL(selectedCertificate)
  : currentCertification?.certificateImage || watch("certificateImage");

 useEffect(() => {
  return () => {
   if (logoPreview?.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
   if (certificatePreview?.startsWith("blob:"))
    URL.revokeObjectURL(certificatePreview);
  };
 }, [logoPreview, certificatePreview]);

 function openAddForm() {
  setEditingCertification("");
  reset(emptyCertification);
  setFormOpen(true);
 }

 function openEditForm(certification) {
  editCertification(certification);
  reset({
   id: certification.id,
   name: certification.name,
   logo: certification.logo,
   certificateImage: certification.certificateImage,
   sortOrder: String(certification.sortOrder || 0),
   active: String(certification.active !== false),
  });
  setFormOpen(true);
 }

 async function submitCertification(values) {
  const saved = await saveCertification(values);
  if (saved) {
   reset(emptyCertification);
   setFormOpen(false);
  }
 }

 const columns = [
  {
   field: "name",
   headerName: "Certification",
   flex: 1.2,
   minWidth: 220,
   renderCell: ({ row }) => (
    <div className="grid-main-cell">
     <strong>{displayText(row.name, "Certification")}</strong>
     <small>{row.id}</small>
    </div>
   ),
  },
  {
   field: "logo",
   headerName: "Logo",
   width: 110,
   sortable: false,
   renderCell: ({ row }) => (
    <span className="grid-thumb">
     {row.logo ? (
      <img src={row.logo} alt={displayText(row.name, "Logo")} />
     ) : (
      <FiImage />
     )}
    </span>
   ),
  },
  {
   field: "certificateImage",
   headerName: "Certificate",
   width: 130,
   sortable: false,
   renderCell: ({ row }) => (
    <span className="grid-thumb banner-thumb">
     {row.certificateImage ? (
      <img
       src={row.certificateImage}
       alt={displayText(row.name, "Certificate")}
      />
     ) : (
      <FiFileText />
     )}
    </span>
   ),
  },
  { field: "sortOrder", headerName: "Order", width: 95 },
  {
   field: "active",
   headerName: "Status",
   width: 130,
   renderCell: ({ row }) => (
    <Chip
     color={row.active === false ? "default" : "success"}
     label={row.active === false ? "Hidden" : "Active"}
     size="small"
     variant="outlined"
    />
   ),
  },
  {
   field: "actions",
   headerName: "Actions",
   width: 130,
   sortable: false,
   align: "center",
   headerAlign: "center",
   renderCell: ({ row }) => (
    <Stack className="grid-action-stack" direction="row" spacing={0.5}>
     <IconAction
      icon={FiEdit3}
      label="Edit certification"
      onClick={() => openEditForm(row)}
     />
     <IconAction
      icon={FiTrash2}
      label="Delete certification"
      onClick={() => deleteCertification(row.id)}
      tone="danger"
     />
    </Stack>
   ),
  },
 ];

 return (
  <section className="page-stack">
   <div className="page-toolbar">
    <div>
     <p>{eyebrow}</p>
     <h1>{pageTitle}</h1>
    </div>
    <button type="button" onClick={openAddForm}>
     <FiPlus /> Add Certification
    </button>
   </div>

   <AdminDialog
    eyebrow={editingCertification ? "Edit certification" : "New certification"}
    onClose={() => setFormOpen(false)}
    open={formOpen}
    title={editingCertification ? "Update certification" : "Add certification"}
   >
    <form className="product-form" onSubmit={handleSubmit(submitCertification)}>
     <label>
      Sort Order
      <input {...register("sortOrder")} min="0" type="number" />
     </label>
     <label>
      Certification Name
      <input {...register("name", { required: true })} placeholder="ISO 9001" />
     </label>
     <label>
      Active
      <select {...register("active")}>
       <option value="true">Active</option>
       <option value="false">Hidden</option>
      </select>
     </label>
     <label className="wide">
      Logo Image URL
      <input {...register("logo")} placeholder="https://..." />
     </label>
     <label className="wide">
      Upload Logo Image
      <input
       {...register("logoFile")}
       accept="image/jpeg,image/png,image/webp,image/gif"
       type="file"
      />
     </label>
     <label className="wide">
      Certificate Image URL
      <input {...register("certificateImage")} placeholder="https://..." />
     </label>
     <label className="wide">
      Upload Certificate Image
      <input
       {...register("certificateFile")}
       accept="image/jpeg,image/png,image/webp,image/gif"
       type="file"
      />
     </label>
     <div className="image-upload-preview wide">
      <div className="grid gap-4 sm:grid-cols-2">
       <div>
        {logoPreview ? (
         <img src={logoPreview} alt="Logo preview" />
        ) : (
         <span>
          <FiImage /> No logo selected
         </span>
        )}
        <small>Logo shown in About page grid.</small>
       </div>
       <div>
        {certificatePreview ? (
         <img src={certificatePreview} alt="Certificate preview" />
        ) : (
         <span>
          <FiFileText /> No certificate selected
         </span>
        )}
        <small>Full certificate shown when user clicks.</small>
       </div>
      </div>
     </div>
     <div className="actions wide">
      <button type="submit" disabled={isSubmitting}>
       <FiSave /> Save Certification
      </button>
      <button
       className="ghost"
       type="button"
       onClick={() => reset(emptyCertification)}
      >
       Clear
      </button>
     </div>
    </form>
   </AdminDialog>

   <article className="panel table-panel">
    <div className="panel-head">
     <div>
      <p>{eyebrow}</p>
      <h2>{pageTitle}</h2>
      <small>{certifications.length} certifications</small>
     </div>
     <div className="filters">
      <button type="button" onClick={openAddForm}>
       <FiPlus /> Add Certification
      </button>
     </div>
    </div>
    <GridShell
     rows={certifications}
     columns={columns}
     getRowId={(row) => row.id}
     emptyText={emptyText}
    />
   </article>
  </section>
 );
}
function CustomerFeedbackPage({
 customerFeedback,
 editingFeedback,
 setEditingFeedback,
 saveFeedback,
 editFeedback,
 deleteFeedback,
}) {
 const [formOpen, setFormOpen] = useState(false);
 const {
  register,
  handleSubmit,
  reset,
  watch,
  formState: { isSubmitting },
 } = useForm({ defaultValues: emptyFeedback });
 const selectedImage = watch("imageFile")?.[0];
 const currentFeedback = customerFeedback.find(
  (item) => item.id === editingFeedback,
 );
 const preview = selectedImage
  ? URL.createObjectURL(selectedImage)
  : currentFeedback?.image || watch("image");

 useEffect(() => {
  if (!selectedImage || !preview?.startsWith("blob:")) return undefined;
  return () => URL.revokeObjectURL(preview);
 }, [preview, selectedImage]);

 function openAddForm() {
  setEditingFeedback("");
  reset(emptyFeedback);
  setFormOpen(true);
 }

 function openEditForm(item) {
  editFeedback(item);
  reset({
   id: item.id,
   name: displayText(item.name),
   company: item.company || "",
   quote: item.quote || "",
   image: item.image || "",
   sortOrder: String(item.sortOrder || 0),
   active: String(item.active !== false),
  });
  setFormOpen(true);
 }

 async function submitFeedback(values) {
  const saved = await saveFeedback(values);
  if (saved) {
   reset(emptyFeedback);
   setFormOpen(false);
  }
 }

 const feedbackColumns = [
  {
   field: "name",
   headerName: "Customer",
   flex: 1.1,
   minWidth: 220,
   renderCell: ({ row }) => (
    <div className="grid-main-cell">
     <strong>{displayText(row.name, "Untitled")}</strong>
     <small>{row.company || row.id}</small>
    </div>
   ),
  },
  {
   field: "image",
   headerName: "Image",
   width: 92,
   sortable: false,
   renderCell: ({ row }) => (
    <span className="grid-thumb">
     {row.image ? (
      <img src={row.image} alt={displayText(row.name, "Image")} />
     ) : (
      <FiImage />
     )}
    </span>
   ),
  },
  {
   field: "quote",
   headerName: "Comment",
   flex: 1.6,
   minWidth: 320,
   renderCell: ({ row }) => <span className="grid-wrap-text">{row.quote}</span>,
  },
  { field: "sortOrder", headerName: "Order", width: 90 },
  {
   field: "active",
   headerName: "Status",
   width: 120,
   renderCell: ({ row }) => (
    <Chip
     color={row.active === false ? "default" : "success"}
     label={row.active === false ? "Hidden" : "Active"}
     size="small"
     variant="outlined"
    />
   ),
  },
  {
   field: "actions",
   headerName: "Actions",
   width: 130,
   sortable: false,
   align: "center",
   headerAlign: "center",
   renderCell: ({ row }) => (
    <Stack className="grid-action-stack" direction="row" spacing={0.5}>
     <IconAction
      icon={FiEdit3}
      label="Edit feedback"
      onClick={() => openEditForm(row)}
     />
     <IconAction
      icon={FiTrash2}
      label="Delete feedback"
      onClick={() => deleteFeedback(row.id)}
      tone="danger"
     />
    </Stack>
   ),
  },
 ];

 return (
  <section className="page-stack">
   <AdminDialog
    eyebrow={editingFeedback ? "Edit feedback" : "New feedback"}
    onClose={() => setFormOpen(false)}
    open={formOpen}
    title={
     editingFeedback ? "Update customer feedback" : "Add customer feedback"
    }
   >
    <form className="product-form" onSubmit={handleSubmit(submitFeedback)}>
     <label>
      Sort Order
      <input {...register("sortOrder")} min="0" type="number" />
     </label>
     <label>
      Customer Name
      <input
       {...register("name", { required: true })}
       placeholder="Customer name"
      />
     </label>
     <label>
      Company
      <input {...register("company")} placeholder="Company name" />
     </label>
     <label>
      Active
      <select {...register("active")}>
       <option value="true">Active</option>
       <option value="false">Hidden</option>
      </select>
     </label>
     <label className="wide">
      Comment
      <textarea
       {...register("quote", { required: true })}
       placeholder="Customer feedback comment"
      />
     </label>
     <label className="wide">
      Image URL
      <input {...register("image")} placeholder="https://..." />
     </label>
     <label className="wide">
      Upload Image
      <input
       {...register("imageFile")}
       accept="image/jpeg,image/png,image/webp,image/gif"
       type="file"
      />
     </label>
     <div className="image-upload-preview wide">
      {preview ? (
       <img src={preview} alt="Feedback preview" />
      ) : (
       <span>
        <FiImage /> No image selected
       </span>
      )}
      <small>Customer image shown in the happy feedback slider.</small>
     </div>
     <div className="actions wide">
      <button type="submit" disabled={isSubmitting}>
       <FiSave /> Save Feedback
      </button>
      <button
       className="ghost"
       type="button"
       onClick={() => reset(emptyFeedback)}
      >
       Clear
      </button>
     </div>
    </form>
   </AdminDialog>

   <article className="panel table-panel">
    <div className="panel-head">
     <div>
      <p>Homepage Content</p>
      <h2>Happy Customer Feedback</h2>
      <small>{customerFeedback.length} feedback items</small>
     </div>
     <div className="filters">
      <button type="button" onClick={openAddForm}>
       <FiPlus /> Add Feedback
      </button>
     </div>
    </div>
    <GridShell
     rows={customerFeedback}
     columns={feedbackColumns}
     getRowId={(row) => row.id}
     emptyText="No customer feedback saved."
    />
   </article>
  </section>
 );
}

function EnquiriesPage({
 enquiries,
 updateEnquiryStatus,
 deleteEnquiry,
 openQuotationHtml,
 openReceiptImage,
}) {
 const [query, setQuery] = useState("");
 const [statusFilter, setStatusFilter] = useState("");

 const filteredEnquiries = enquiries.filter((enquiry) => {
  const term = query.trim().toLowerCase();
  const customer = enquiry.customer || {};
  const quoteText =
   enquiry.quote?.items
    ?.map((item) => `${displayText(item.name)} ${item.productId}`)
    .join(" ") || "";
  const matchesSearch = term
   ? `${enquiry.enquiryId} ${customer.name || ""} ${customer.phone || ""} ${customer.email || ""} ${quoteText}`
      .toLowerCase()
      .includes(term)
   : true;
  const matchesStatus = statusFilter ? enquiry.status === statusFilter : true;

  return matchesSearch && matchesStatus;
 });

 const enquiryColumns = [
  {
   field: "customer",
   headerName: "Customer",
   flex: 1.2,
   minWidth: 210,
   renderCell: ({ row }) => {
    const customer = row.customer || {};
    return (
     <div className="grid-main-cell">
      <strong>{customer.name || "Customer"}</strong>
      <small>
       {row.enquiryId} - {row.type === "contact" ? "Contact" : "Quote"}
      </small>
      <small>{customer.phone || "No phone"}</small>
      <small>{customer.email || "No email"}</small>
     </div>
    );
   },
  },
  {
   field: "products",
   headerName: "Quoted Products",
   flex: 1.45,
   minWidth: 260,
   sortable: false,
   renderCell: ({ row }) => {
    const items = row.quote?.items || [];
    return items.length ? (
     <div className="grid-mini-list">
      {items.slice(0, 3).map((item) => (
       <span key={`${row.enquiryId}-${item.productId}`}>
        <b>{displayText(item.name)}</b>
        <em>x {item.quantity}</em>
        <strong>{currency(item.lineTotal)}</strong>
       </span>
      ))}
      {items.length > 3 ? <small>+{items.length - 3} more items</small> : null}
     </div>
    ) : (
     <span className="muted-text">No quoted products</span>
    );
   },
  },
  {
   field: "total",
   headerName: "Total",
   width: 112,
   valueGetter: (_value, row) =>
    row.type === "contact" ? "General" : currency(row.quote?.total),
  },
  {
   field: "status",
   headerName: "Status",
   width: 112,
   renderCell: ({ row }) => (
    <Chip
     color={
      row.status === "new"
       ? "primary"
       : row.status === "closed"
         ? "default"
         : "success"
     }
     label={row.status}
     size="small"
     variant="outlined"
    />
   ),
  },
  {
   field: "actions",
   headerName: "Actions",
   width: 360,
   minWidth: 360,
   sortable: false,
   renderCell: ({ row }) => {
    const customer = row.customer || {};
    const phoneHref = customer.phone ? `tel:${customer.phone}` : undefined;
    const mailHref = customer.email ? `mailto:${customer.email}` : undefined;
    const isContact = row.type === "contact";
    const whatsappHref = buildAdminWhatsappUrl(row);

    return (
     <div className="enquiry-grid-actions">
      <Tooltip title="Call customer">
       <span>
        <IconButton
         component="a"
         disabled={!phoneHref}
         href={phoneHref || "#"}
         size="small"
         aria-label="Call customer"
        >
         <FiPhone />
        </IconButton>
       </span>
      </Tooltip>
      <Tooltip title="Email customer">
       <span>
        <IconButton
         component="a"
         disabled={!mailHref}
         href={mailHref || "#"}
         size="small"
         aria-label="Email customer"
        >
         <FiMail />
        </IconButton>
       </span>
      </Tooltip>
      {!isContact && whatsappHref ? (
       <Tooltip title="WhatsApp quote">
        <IconButton
         component="a"
         href={whatsappHref}
         rel="noreferrer"
         size="small"
         target="_blank"
         aria-label="WhatsApp quote"
        >
         <FaWhatsapp />
        </IconButton>
       </Tooltip>
      ) : null}
      {!isContact ? (
       <Tooltip title="Open quotation">
        <IconButton
         className="enquiry-primary-action"
         onClick={() => openQuotationHtml(row)}
         size="small"
         aria-label="Open quotation"
        >
         <FiFileText />
        </IconButton>
       </Tooltip>
      ) : null}
      {!isContact ? (
       <Tooltip title="Open receipt image">
        <IconButton
         className="enquiry-primary-action"
         onClick={() => openReceiptImage(row)}
         size="small"
         aria-label="Open receipt image"
        >
         <FiImage />
        </IconButton>
       </Tooltip>
      ) : null}
      <Tooltip title="Mark contacted">
       <IconButton
        className="enquiry-primary-action"
        onClick={() => updateEnquiryStatus(row.enquiryId, "contacted")}
        size="small"
        aria-label="Mark contacted"
       >
        <FiCheckCircle />
       </IconButton>
      </Tooltip>
      <Tooltip title="Close enquiry">
       <IconButton
        onClick={() => updateEnquiryStatus(row.enquiryId, "closed")}
        size="small"
        aria-label="Close enquiry"
       >
        <FiXCircle />
       </IconButton>
      </Tooltip>
      <Tooltip title="Delete enquiry">
       <IconButton
        className="enquiry-danger-action"
        onClick={() => deleteEnquiry(row.enquiryId)}
        size="small"
        aria-label="Delete enquiry"
       >
        <FiTrash2 />
       </IconButton>
      </Tooltip>
     </div>
    );
   },
  },
 ];

 return (
  <section className="page-stack">
   <div className="page-toolbar">
    <div>
     <p>Customer Requests</p>
     <h1>Enquiries</h1>
    </div>
   </div>

   <article className="panel table-panel">
    <div className="panel-head table-head">
     <div>
      <p>Customer Requests</p>
      <h2>Enquiries</h2>
      <small>
       {filteredEnquiries.length} of {enquiries.length} requests
      </small>
     </div>
     <div className="filters product-filters">
      <label>
       <FiSearch />
       <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search customer or item"
       />
      </label>
      <select
       value={statusFilter}
       onChange={(event) => setStatusFilter(event.target.value)}
      >
       <option value="">All status</option>
       <option value="new">New</option>
       <option value="contacted">Contacted</option>
       <option value="closed">Closed</option>
      </select>
     </div>
    </div>
    <GridShell
     rows={filteredEnquiries}
     columns={enquiryColumns}
     getRowId={(row) => row.enquiryId}
     emptyText="No website enquiries match this filter."
     rowHeight={92}
    />
    <div className="enquiry-table-wrap legacy-hidden" aria-hidden="true">
     <div className="enquiry-table enquiry-table-header">
      <span>Customer</span>
      <span>Requirement</span>
      <span>Quoted Products</span>
      <span>Total</span>
      <span>Status</span>
      <span>Actions</span>
     </div>
     {filteredEnquiries.map((enquiry) => {
      const customer = enquiry.customer || {};
      const phoneHref = customer.phone ? `tel:${customer.phone}` : undefined;
      const mailHref = customer.email ? `mailto:${customer.email}` : undefined;
      const message = customer.message || "No message added.";
      const isContact = enquiry.type === "contact";
      const whatsappHref = buildAdminWhatsappUrl(enquiry);
      const quoteItems = enquiry.quote?.items || [];

      return (
       <article
        className="enquiry-table enquiry-table-row"
        key={enquiry.enquiryId}
       >
        <div>
         <strong>{customer.name || "Customer"}</strong>
         <small>
          {enquiry.enquiryId} -{" "}
          {isContact ? "Contact enquiry" : "Quote enquiry"}
         </small>
         <small>{customer.companyName || "No company"}</small>
         <small>{customer.phone || "No phone"}</small>
         <small>{customer.email || "No email"}</small>
         <small>{customer.address || "No address"}</small>
        </div>

        <div className="enquiry-message-cell">{message}</div>

        <div className="quote-mini-table">
         {!isContact && quoteItems.length ? (
          <>
           <div className="quote-mini-head">
            <span>Item</span>
            <span>Qty</span>
            <span>Amount</span>
           </div>
           {quoteItems.map((item) => (
            <div
             className="quote-mini-row"
             key={`${enquiry.enquiryId}-${item.productId}`}
            >
             <span>{displayText(item.name)}</span>
             <span>{item.quantity}</span>
             <strong>{currency(item.lineTotal)}</strong>
            </div>
           ))}
          </>
         ) : (
          <span className="muted-text">No quoted products</span>
         )}
        </div>

        <strong className="total-cell">
         {isContact
          ? customer.companyName || "General"
          : currency(enquiry.quote?.total)}
        </strong>

        <em className={`status-pill ${enquiry.status}`}>{enquiry.status}</em>

        <div className="enquiry-actions compact">
         <a
          className={!phoneHref ? "disabled-link" : ""}
          href={phoneHref || "#"}
          aria-disabled={!phoneHref}
         >
          <FiPhone /> Call
         </a>
         <a
          className={!mailHref ? "disabled-link" : ""}
          href={mailHref || "#"}
          aria-disabled={!mailHref}
         >
          <FiMail /> Email
         </a>
         {!isContact && whatsappHref ? (
          <a href={whatsappHref} target="_blank" rel="noreferrer">
           <FiPhone /> WA
          </a>
         ) : null}
         {!isContact ? (
          <>
           <button type="button" onClick={() => openQuotationHtml(enquiry)}>
            <FiFileText /> Quote
           </button>
           <button type="button" onClick={() => openReceiptImage(enquiry)}>
            <FiImage /> Receipt
           </button>
          </>
         ) : null}
         <button
          type="button"
          onClick={() => updateEnquiryStatus(enquiry.enquiryId, "contacted")}
         >
          Contacted
         </button>
         <button
          className="ghost"
          type="button"
          onClick={() => updateEnquiryStatus(enquiry.enquiryId, "closed")}
         >
          Close
         </button>
         <button
          className="danger"
          type="button"
          onClick={() => deleteEnquiry(enquiry.enquiryId)}
         >
          <FiTrash2 /> Delete
         </button>
        </div>
       </article>
      );
     })}

     {filteredEnquiries.length === 0 ? (
      <div className="empty-state">No website enquiries match this filter.</div>
     ) : null}
    </div>
   </article>
  </section>
 );
}

function App() {
 const dispatch = useDispatch();
 const {
  categories,
  products,
  productPagination,
  enquiries,
  sliders,
  aboutSliders,
  homeJourneyImages,
  homeCommunityImages,
  customerFeedback,
  scootyFeatureConfig,
  systemCertifications,
  productCertifications,
  status: adminStatus,
  loading: adminLoading,
 } = useSelector((state) => state.admin);
 const [activePage, setActivePage] = useState("dashboard");
 const [token, setToken] = useState(
  () => localStorage.getItem("mozoAdminToken") || "",
 );
 const [user, setUser] = useState(() => {
  const cached = localStorage.getItem("mozoAdminUser");
  return cached ? JSON.parse(cached) : null;
 });
 const [loginForm, setLoginForm] = useState(emptyLogin);
 const [editingCategory, setEditingCategory] = useState("");
 const [editingProduct, setEditingProduct] = useState("");
 const [editingSlider, setEditingSlider] = useState("");
 const [editingAboutSlider, setEditingAboutSlider] = useState("");
 const [editingJourneyImage, setEditingJourneyImage] = useState("");
 const [editingCommunityImage, setEditingCommunityImage] = useState("");
 const [editingFeedback, setEditingFeedback] = useState("");
 const [editingSystemCertification, setEditingSystemCertification] =
  useState("");
 const [editingProductCertification, setEditingProductCertification] =
  useState("");
 const [query, setQuery] = useState("");
 const [selectedCategory, setSelectedCategory] = useState("");
 const [priceFilter, setPriceFilter] = useState("");
 const [status, setStatus] = useState({
  type: "idle",
  text: "Sign in to continue.",
 });
 const [theme, setTheme] = useState(() => {
  const savedTheme = localStorage.getItem("mozoAdminTheme");
  if (savedTheme === "mono") return "dark";
  if (savedTheme === "mozo") return "light";
  return savedTheme || "light";
 });
 const [sidebarCollapsed, setSidebarCollapsed] = useState(
  () => localStorage.getItem("mozoSidebarCollapsed") === "true",
 );
 const [loading, setLoading] = useState(false);

 useEffect(() => {
  document.documentElement.setAttribute("data-theme", theme);
  localStorage.setItem("mozoAdminTheme", theme);
 }, [theme]);

 useEffect(() => {
  localStorage.setItem("mozoSidebarCollapsed", String(sidebarCollapsed));
 }, [sidebarCollapsed]);

 const authHeaders = useMemo(
  () => ({
   ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }),
  [token],
 );

 const filteredProducts = products.filter((product) => {
  const matchesPrice = priceFilter
   ? Number(product.price || 0) <= Number(priceFilter)
   : true;

  return matchesPrice;
 });

 const totals = useMemo(() => {
  const value = products.reduce(
   (sum, product) =>
    sum + Number(product.price || 0) * Number(product.stock || 0),
   0,
  );
  const openEnquiries = enquiries.filter(
   (enquiry) => enquiry.status !== "closed",
  ).length;

  return { value, openEnquiries };
 }, [products, enquiries]);

 const request = useCallback(
  async (path, options = {}) => {
   const isFormData = isFormDataBody(options.body);
   const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
     ...(!isFormData && options.body
      ? { "Content-Type": "application/json" }
      : {}),
     ...authHeaders,
     ...(options.headers || {}),
    },
   });
   const data = await response.json().catch(() => ({}));

   if (!response.ok) {
    throw new Error(data.error || "API request failed");
   }

   return data;
  },
  [authHeaders],
 );

 const openQuotationHtml = useCallback((enquiry) => {
  const quotationWindow = window.open("", "_blank");
  if (!quotationWindow) {
   setStatus({ type: "error", text: "Please allow popups to open quotation." });
   return;
  }

  quotationWindow.document.open();
  quotationWindow.document.write(buildAdminQuotationHtml(enquiry));
  quotationWindow.document.close();
 }, []);

 const openReceiptImage = useCallback(
  async (enquiry) => {
   try {
    const response = await fetch(
     `${API_BASE}/enquiries/${enquiry.enquiryId}/receipt.svg`,
     { headers: authHeaders },
    );

    if (!response.ok) {
     throw new Error("Unable to generate receipt image.");
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const receiptWindow = window.open(url, "_blank", "noopener,noreferrer");

    if (!receiptWindow) {
     setStatus({
      type: "error",
      text: "Please allow popups to open receipt image.",
     });
    }

    setTimeout(() => URL.revokeObjectURL(url), 60000);
   } catch (error) {
    setStatus({ type: "error", text: error.message });
   }
  },
  [authHeaders],
 );
 const loadAdminData = useCallback(async () => {
  if (!token) return;
  await dispatch(loadAdminDataAction());
 }, [dispatch, token]);

 useEffect(() => {
  loadAdminData();
 }, [loadAdminData]);

 async function login(event) {
  event.preventDefault();
  setLoading(true);
  try {
   const data = await request("/auth/login", {
    method: "POST",
    body: JSON.stringify(loginForm),
   });

   localStorage.setItem("mozoAdminToken", data.token);
   localStorage.setItem("mozoAdminUser", JSON.stringify(data.user));
   setToken(data.token);
   setUser(data.user);
   dispatch(setCredentials({ token: data.token, user: data.user }));
   setActivePage("dashboard");
   setStatus({ type: "success", text: `Logged in as ${data.user.email}` });
  } catch (error) {
   setStatus({ type: "error", text: error.message });
  } finally {
   setLoading(false);
  }
 }
 function logout() {
  localStorage.removeItem("mozoAdminToken");
  localStorage.removeItem("mozoAdminUser");
  setToken("");
  setUser(null);
  dispatch(logoutAdmin());
  setActivePage("dashboard");
  setStatus({ type: "idle", text: "Sign in to continue." });
 }

 async function saveCategory(values) {
  const payload = createFormData({
   ...values,
   id: editingCategory || buildEntityId("category", values.name),
   displayOrder: Number(values.displayOrder || 0),
  });

  try {
   await request(
    editingCategory ? `/categories/${editingCategory}` : "/categories",
    {
     method: editingCategory ? "PUT" : "POST",
     body: payload,
    },
   );
   setEditingCategory("");
   setStatus({ type: "success", text: "Category saved." });
   await loadAdminData();
   return true;
  } catch (error) {
   setStatus({ type: "error", text: error.message });
   return false;
  }
 }

 async function saveProduct(values) {
  const payload = createFormData(
   {
    ...values,
    name: plainInputText(values.name),
    spec: plainInputText(values.spec),
    id: editingProduct || buildEntityId("product", plainInputText(values.name)),
    price: Number(values.price),
    rating: Number(values.rating || 4.5),
    discountType: values.discountType || "none",
    discountValue: Number(values.discountValue || 0),
    gstPercent: Number(values.gstPercent || 0),
    minQty: Number(values.minQty),
    unit: values.unit || "pcs",
    optionGroupName: values.optionGroupName || "Size",
    variants: values.variants || "[]",
    specifications: values.specifications || "[]",
    applications: values.applications || "[]",
    features: values.features || "[]",
    benefits: values.benefits || "[]",
    sizes: values.sizes || "[]",
    stock: Number(values.stock),
   },
   "images",
  );

  try {
   await request(editingProduct ? `/products/${editingProduct}` : "/products", {
    method: editingProduct ? "PUT" : "POST",
    body: payload,
   });
   setEditingProduct("");
   setStatus({ type: "success", text: "Product saved." });
   await reloadProductsWithActiveFilters();
   return true;
  } catch (error) {
   setStatus({ type: "error", text: error.message });
   return false;
  }
 }

 async function saveSlider(values) {
  const payload = new FormData();
  payload.append("title", values.title || "");
  payload.append(
   "image",
   values.imageFile?.length ? values.imageFile[0] : values.image || "",
  );
  payload.append("sortOrder", Number(values.sortOrder || 0));
  payload.append("active", values.active || "true");

  try {
   await request(
    editingSlider ? `/admin/sliders/${editingSlider}` : "/admin/sliders",
    {
     method: editingSlider ? "PUT" : "POST",
     body: payload,
    },
   );
   setEditingSlider("");
   setStatus({ type: "success", text: "Slider saved." });
   await loadAdminData();
   return true;
  } catch (error) {
   setStatus({ type: "error", text: error.message });
   return false;
  }
 }
 async function saveAboutSlider(values) {
  const payload = new FormData();
  payload.append("title", values.title || "");
  payload.append(
   "image",
   values.imageFile?.length ? values.imageFile[0] : values.image || "",
  );
  payload.append("sortOrder", Number(values.sortOrder || 0));
  payload.append("active", values.active || "true");

  try {
   await request(
    editingAboutSlider
     ? `/admin/about-sliders/${editingAboutSlider}`
     : "/admin/about-sliders",
    {
     method: editingAboutSlider ? "PUT" : "POST",
     body: payload,
    },
   );
   setEditingAboutSlider("");
   setStatus({ type: "success", text: "About slider saved." });
   await loadAdminData();
   return true;
  } catch (error) {
   setStatus({ type: "error", text: error.message });
   return false;
  }
 }

 async function saveHomeImage(section, editingId, setEditingId, values) {
  const payload = new FormData();
  payload.append("title", values.title || "");
  payload.append(
   "image",
   values.imageFile?.length ? values.imageFile[0] : values.image || "",
  );
  payload.append("sortOrder", Number(values.sortOrder || 0));
  payload.append("active", values.active || "true");

  try {
   await request(
    editingId
     ? `/admin/home-images/${section}/${editingId}`
     : `/admin/home-images/${section}`,
    {
     method: editingId ? "PUT" : "POST",
     body: payload,
    },
   );
   setEditingId("");
   setStatus({ type: "success", text: "Home image saved." });
   await loadAdminData();
   return true;
  } catch (error) {
   setStatus({ type: "error", text: error.message });
   return false;
  }
 }

 function saveJourneyImage(values) {
  return saveHomeImage(
   "journey",
   editingJourneyImage,
   setEditingJourneyImage,
   values,
  );
 }

 function saveCommunityImage(values) {
  return saveHomeImage(
   "community",
   editingCommunityImage,
   setEditingCommunityImage,
   values,
  );
 }

 async function saveFeedback(values) {
  const payload = new FormData();
  payload.append("name", values.name || "");
  payload.append("company", values.company || "");
  payload.append("quote", values.quote || "");
  payload.append(
   "image",
   values.imageFile?.length ? values.imageFile[0] : values.image || "",
  );
  payload.append("sortOrder", Number(values.sortOrder || 0));
  payload.append("active", values.active || "true");

  try {
   await request(
    editingFeedback
     ? `/admin/customer-feedback/${editingFeedback}`
     : "/admin/customer-feedback",
    {
     method: editingFeedback ? "PUT" : "POST",
     body: payload,
    },
   );
   setEditingFeedback("");
   setStatus({ type: "success", text: "Customer feedback saved." });
   await loadAdminData();
   return true;
  } catch (error) {
   setStatus({ type: "error", text: error.message });
   return false;
  }
 }

 async function saveScootyFeatureConfig(config) {
  try {
   await request("/admin/scooty-feature-config", {
    method: "PUT",
    body: JSON.stringify(config),
   });
   setStatus({ type: "success", text: "Scooty feature layout saved." });
   await loadAdminData();
   return true;
  } catch (error) {
   setStatus({ type: "error", text: error.message });
   return false;
  }
 }
 async function saveCertification(section, editingId, setEditingId, values) {
  const payload = new FormData();
  payload.append("name", values.name || "");
  payload.append("logo", values.logo || "");
  payload.append("certificateImage", values.certificateImage || "");
  if (values.logoFile?.length) payload.append("image", values.logoFile[0]);
  if (values.certificateFile?.length)
   payload.append("images", values.certificateFile[0]);
  payload.append("sortOrder", Number(values.sortOrder || 0));
  payload.append("active", values.active || "true");

  try {
   await request(
    editingId
     ? `/admin/certifications/${section}/${editingId}`
     : `/admin/certifications/${section}`,
    {
     method: editingId ? "PUT" : "POST",
     body: payload,
    },
   );
   setEditingId("");
   setStatus({ type: "success", text: "Certification saved." });
   await loadAdminData();
   return true;
  } catch (error) {
   setStatus({ type: "error", text: error.message });
   return false;
  }
 }

 function saveSystemCertification(values) {
  return saveCertification(
   "system",
   editingSystemCertification,
   setEditingSystemCertification,
   values,
  );
 }

 function saveProductCertification(values) {
  return saveCertification(
   "product",
   editingProductCertification,
   setEditingProductCertification,
   values,
  );
 }
 async function deleteCategory(categoryId) {
  if (!window.confirm(`Delete category "${categoryId}"?`)) return;

  try {
   await request(`/categories/${categoryId}`, { method: "DELETE" });
   setStatus({ type: "success", text: "Category deleted." });
   await loadAdminData();
  } catch (error) {
   setStatus({ type: "error", text: error.message });
  }
 }

 async function deleteProduct(productId) {
  if (!window.confirm(`Delete product "${productId}"?`)) return;

  try {
   await request(`/products/${productId}`, { method: "DELETE" });
   setStatus({ type: "success", text: "Product deleted." });
   await reloadProductsWithActiveFilters();
  } catch (error) {
   setStatus({ type: "error", text: error.message });
  }
 }

 async function updateProductStock(productId, stock) {
  try {
   await request(`/products/${productId}/stock`, {
    method: "PATCH",
    body: JSON.stringify({ stock: Number(stock || 0) }),
   });
   setStatus({ type: "success", text: "Stock updated." });
   await reloadProductsWithActiveFilters();
  } catch (error) {
   setStatus({ type: "error", text: error.message });
  }
 }

 async function deleteSlider(sliderId) {
  if (!window.confirm(`Delete slider "${sliderId}"?`)) return;

  try {
   await request(`/admin/sliders/${sliderId}`, { method: "DELETE" });
   setStatus({ type: "success", text: "Slider deleted." });
   await loadAdminData();
  } catch (error) {
   setStatus({ type: "error", text: error.message });
  }
 }

 async function deleteAboutSlider(sliderId) {
  if (!window.confirm(`Delete about slider "${sliderId}"?`)) return;

  try {
   await request(`/admin/about-sliders/${sliderId}`, { method: "DELETE" });
   setStatus({ type: "success", text: "About slider deleted." });
   await loadAdminData();
  } catch (error) {
   setStatus({ type: "error", text: error.message });
  }
 }

 async function deleteHomeImage(section, imageId) {
  if (!window.confirm(`Delete home image "${imageId}"?`)) return;

  try {
   await request(`/admin/home-images/${section}/${imageId}`, {
    method: "DELETE",
   });
   setStatus({ type: "success", text: "Home image deleted." });
   await loadAdminData();
  } catch (error) {
   setStatus({ type: "error", text: error.message });
  }
 }

 function deleteJourneyImage(imageId) {
  return deleteHomeImage("journey", imageId);
 }

 function deleteCommunityImage(imageId) {
  return deleteHomeImage("community", imageId);
 }

 async function deleteFeedback(feedbackId) {
  if (!window.confirm(`Delete customer feedback "${feedbackId}"?`)) return;

  try {
   await request(`/admin/customer-feedback/${feedbackId}`, {
    method: "DELETE",
   });
   setStatus({ type: "success", text: "Customer feedback deleted." });
   await loadAdminData();
  } catch (error) {
   setStatus({ type: "error", text: error.message });
  }
 }

 async function deleteCertification(section, certificationId) {
  if (!window.confirm(`Delete certification "${certificationId}"?`)) return;

  try {
   await request(`/admin/certifications/${section}/${certificationId}`, {
    method: "DELETE",
   });
   setStatus({ type: "success", text: "Certification deleted." });
   await loadAdminData();
  } catch (error) {
   setStatus({ type: "error", text: error.message });
  }
 }

 function deleteSystemCertification(certificationId) {
  return deleteCertification("system", certificationId);
 }

 function deleteProductCertification(certificationId) {
  return deleteCertification("product", certificationId);
 }
 async function updateEnquiryStatus(enquiryId, nextStatus) {
  try {
   await request(`/enquiries/${enquiryId}/status`, {
    method: "PUT",
    body: JSON.stringify({ status: nextStatus }),
   });
   setStatus({ type: "success", text: `Enquiry marked ${nextStatus}.` });
   await loadAdminData();
  } catch (error) {
   setStatus({ type: "error", text: error.message });
  }
 }

 function editCategory(category) {
  setEditingCategory(category.id);
 }

 function editProduct(product) {
  setEditingProduct(product.id);
  setActivePage("products");
 }

 function editSlider(slider) {
  setEditingSlider(slider.id);
  setActivePage("sliders");
 }

 function editAboutSlider(slider) {
  setEditingAboutSlider(slider.id);
  setActivePage("aboutSliders");
 }

 function editJourneyImage(image) {
  setEditingJourneyImage(image.id);
  setActivePage("journeyImages");
 }

 function editCommunityImage(image) {
  setEditingCommunityImage(image.id);
  setActivePage("communityImages");
 }

 function editFeedback(feedback) {
  setEditingFeedback(feedback.id);
  setActivePage("customerFeedback");
 }
 function editSystemCertification(certification) {
  setEditingSystemCertification(certification.id);
  setActivePage("systemCertifications");
 }

 function editProductCertification(certification) {
  setEditingProductCertification(certification.id);
  setActivePage("productCertifications");
 }
 function dismissStatus(_event, reason) {
  if (reason === "clickaway") return;
  setStatus((current) => ({ ...current, text: "" }));
  dispatch(clearAdminStatus());
 }

 async function deleteEnquiry(enquiryId) {
  if (!window.confirm(`Delete enquiry "${enquiryId}"?`)) return;
  try {
   await dispatch(deleteEnquiryAction(enquiryId)).unwrap();
  } catch (error) {
   setStatus({
    type: "error",
    text: error.message || "Failed to delete enquiry.",
   });
  }
 }

 function getActiveProductPageParams(params = {}) {
  return {
   page: Number(productPagination?.page || 1),
   limit: Number(productPagination?.limit || 100),
   category: selectedCategory,
   q: query,
   maxPrice: priceFilter,
   ...params,
  };
 }

 function reloadProductsWithActiveFilters(params = {}) {
  return dispatch(
   loadProductsPageAction(getActiveProductPageParams(params)),
  ).unwrap();
 }

 function loadProductsPage(params = {}) {
  dispatch(loadProductsPageAction(getActiveProductPageParams(params)));
 }

 async function updateCategorySequence(categoryId, direction) {
  const orderedCategories = [...categories].sort((a, b) => {
   const orderDiff = Number(a.displayOrder || 0) - Number(b.displayOrder || 0);
   return orderDiff || String(a.name || "").localeCompare(String(b.name || ""));
  });
  const currentIndex = orderedCategories.findIndex(
   (category) => category.id === categoryId,
  );
  const targetIndex = currentIndex + direction;

  if (
   currentIndex < 0 ||
   targetIndex < 0 ||
   targetIndex >= orderedCategories.length
  )
   return;

  const nextCategories = [...orderedCategories];
  const [movedCategory] = nextCategories.splice(currentIndex, 1);
  nextCategories.splice(targetIndex, 0, movedCategory);

  try {
   await dispatch(
    updateCategoryOrderAction(
     nextCategories.map((category, index) => ({
      id: category.id,
      displayOrder: index,
     })),
    ),
   ).unwrap();
  } catch (error) {
   setStatus({
    type: "error",
    text: error.message || "Failed to update category sequence.",
   });
  }
 }

 async function updateProductSequence(productId, direction) {
  const currentIndex = products.findIndex(
   (product) => product.id === productId,
  );
  const targetIndex = currentIndex + direction;

  if (currentIndex < 0 || targetIndex < 0 || targetIndex >= products.length)
   return;

  const nextProducts = [...products];
  const [movedProduct] = nextProducts.splice(currentIndex, 1);
  nextProducts.splice(targetIndex, 0, movedProduct);

  try {
   await dispatch(
    updateProductOrderAction(
     nextProducts.map((product, index) => ({
      id: product.id,
      displayOrder:
       (Number(productPagination?.page || 1) - 1) *
        Number(productPagination?.limit || 100) +
       index,
     })),
    ),
   ).unwrap();
  } catch (error) {
   setStatus({
    type: "error",
    text: error.message || "Failed to update product sequence.",
   });
  }
 }

 if (!token || !user) {
  return (
   <>
    <LoginPage
     loginForm={loginForm}
     setLoginForm={setLoginForm}
     login={login}
     loading={loading}
    />
    <Notice status={status} onClose={dismissStatus} />
   </>
  );
 }

 const pageProps = {
  categories,
  products,
  productPagination,
  enquiries,
  sliders,
  aboutSliders,
  homeJourneyImages,
  homeCommunityImages,
  customerFeedback,
  scootyFeatureConfig,
  saveScootyFeatureConfig,
  totals,
  setActivePage,
  editingSlider,
  setEditingSlider,
  saveSlider,
  editSlider,
  deleteSlider,
  editingAboutSlider,
  setEditingAboutSlider,
  saveAboutSlider,
  editAboutSlider,
  deleteAboutSlider,
  editingJourneyImage,
  setEditingJourneyImage,
  saveJourneyImage,
  editJourneyImage,
  deleteJourneyImage,
  editingCommunityImage,
  setEditingCommunityImage,
  saveCommunityImage,
  editCommunityImage,
  deleteCommunityImage,
  editingFeedback,
  setEditingFeedback,
  saveFeedback,
  editFeedback,
  deleteFeedback,
  editingCategory,
  setEditingCategory,
  saveCategory,
  editCategory,
  deleteCategory,
  updateCategorySequence,
  filteredProducts,
  editingProduct,
  setEditingProduct,
  saveProduct,
  editProduct,
  deleteProduct,
  loadProductsPage,
  updateProductStock,
  query,
  setQuery,
  selectedCategory,
  setSelectedCategory,
  priceFilter,
  setPriceFilter,
  updateEnquiryStatus,
  deleteEnquiry,
  updateProductSequence,
  openQuotationHtml,
  openReceiptImage,
 };
 const displayStatus = status.text ? status : adminStatus;
 const pageLoading = loading || adminLoading;

 return (
  <div
   className="app-shell"
   style={{
    display: "flex",
    minHeight: "100vh",
    backgroundColor: "var(--bg)",
   }}
  >
   {/* Sidebar Section */}
   <Sidebar
    activePage={activePage}
    setActivePage={setActivePage}
    user={user}
    logout={logout}
    pages={pages}
    collapsed={sidebarCollapsed}
    setCollapsed={setSidebarCollapsed}
   />

   {/* Main Content Area */}
   <main
    className="admin-main"
    style={{
     flex: 1,
     minWidth: 0 /* Fixes grid overflow bug */,
     marginLeft: sidebarCollapsed ? "92px" : "288px",
     padding: "24px 32px",
     display: "flex",
     flexDirection: "column",
     gap: "20px",
    }}
   >
    {/* Topbar with Header & Refresh Button */}
    <header
     className="topbar"
     style={{
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      paddingBottom: "16px",
      borderBottom: "1px solid var(--line)",
     }}
    >
     <div className="theme-switch" aria-label="Theme switcher">
      <button
       className={theme === "light" ? "active" : ""}
       type="button"
       onClick={() => setTheme("light")}
       aria-pressed={theme === "light"}
      >
       <FiSun /> Light
      </button>
      <button
       className={theme === "dark" ? "active" : ""}
       type="button"
       onClick={() => setTheme("dark")}
       aria-pressed={theme === "dark"}
      >
       <FiMoon /> Dark
      </button>
     </div>
     <button
      className="refresh-button"
      type="button"
      onClick={loadAdminData}
      disabled={pageLoading}
      style={{
       display: "inline-flex",
       alignItems: "center",
       gap: "8px",
       padding: "10px 18px",
       border: "none",
       borderRadius: "8px",
       fontWeight: 600,
       fontSize: "14px",
       cursor: pageLoading ? "not-allowed" : "pointer",
       boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
       transition: "all 0.2s ease",
      }}
     >
      <FiRefreshCw
       style={{
        animation: pageLoading ? "spin 1s linear infinite" : "none",
       }}
      />
      {pageLoading ? "Refreshing..." : "Refresh"}
     </button>
    </header>

    {/* Dynamic Pages */}
    <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
     {activePage === "dashboard" && <DashboardPage {...pageProps} />}
     {activePage === "sliders" && <SlidersPage {...pageProps} />}
     {activePage === "aboutSliders" && (
      <SlidersPage
       sliders={aboutSliders}
       editingSlider={editingAboutSlider}
       setEditingSlider={setEditingAboutSlider}
       saveSlider={saveAboutSlider}
       editSlider={editAboutSlider}
       deleteSlider={deleteAboutSlider}
       eyebrow="About Page Slider"
       pageTitle="About Slider"
       titlePlaceholder="About page image 1"
       recommendedSize="900 x 900 px"
       emptyText="No about slider images saved."
      />
     )}
     {activePage === "journeyImages" && (
      <SlidersPage
       sliders={homeJourneyImages}
       editingSlider={editingJourneyImage}
       setEditingSlider={setEditingJourneyImage}
       saveSlider={saveJourneyImage}
       editSlider={editJourneyImage}
       deleteSlider={deleteJourneyImage}
       eyebrow="Homepage Content"
       pageTitle="Journey Images"
       titlePlaceholder="Journey image 1"
       recommendedSize="1600 x 720 px"
       emptyText="No journey images saved."
      />
     )}
     {activePage === "communityImages" && (
      <SlidersPage
       sliders={homeCommunityImages}
       editingSlider={editingCommunityImage}
       setEditingSlider={setEditingCommunityImage}
       saveSlider={saveCommunityImage}
       editSlider={editCommunityImage}
       deleteSlider={deleteCommunityImage}
       eyebrow="Homepage Content"
       pageTitle="Community Images"
       titlePlaceholder="Community image 1"
       recommendedSize="900 x 620 px"
       emptyText="No community images saved."
      />
     )}
     {activePage === "customerFeedback" && (
      <CustomerFeedbackPage {...pageProps} />
     )}
     {activePage === "scootyFeatures" && (
      <ScootyFeatureEditor
       config={scootyFeatureConfig}
       saveConfig={saveScootyFeatureConfig}
      />
     )}
     {activePage === "systemCertifications" && (
      <CertificationsPage
       certifications={systemCertifications}
       editingCertification={editingSystemCertification}
       setEditingCertification={setEditingSystemCertification}
       saveCertification={saveSystemCertification}
       editCertification={editSystemCertification}
       deleteCertification={deleteSystemCertification}
       eyebrow="About Page"
       pageTitle="System Certifications"
       emptyText="No system certifications saved."
      />
     )}
     {activePage === "productCertifications" && (
      <CertificationsPage
       certifications={productCertifications}
       editingCertification={editingProductCertification}
       setEditingCertification={setEditingProductCertification}
       saveCertification={saveProductCertification}
       editCertification={editProductCertification}
       deleteCertification={deleteProductCertification}
       eyebrow="About Page"
       pageTitle="Product Certifications"
       emptyText="No product certifications saved."
      />
     )}{" "}
     {activePage === "categories" && <CategoriesPage {...pageProps} />}
     {activePage === "products" && <ProductsPage {...pageProps} />}
     {activePage === "stock" && <StockPage {...pageProps} />}
     {activePage === "enquiries" && <EnquiriesPage {...pageProps} />}
    </div>
   </main>
   <Notice status={displayStatus} onClose={dismissStatus} />
  </div>
 );
}

export default App;
